import React from 'react';
import { Card, Col, OverlayTrigger, Popover, Row, Tab, Table, Tabs, Tooltip } from 'react-bootstrap';
import Moment from 'react-moment';
import {
  Bloodwork,
  BloodworkAnalysis,
  BloodworkRange,
  Ethnicity,
  Menopause,
  PanelField,
  FurtherConsiderations,
  PanelMarker,
  Section,
} from '../../models';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import moment from 'moment';
import { validate, ValidationError } from 'class-validator';
import { UserProfile } from '../../models/UserProfile';
import { DisclaimerSection } from './disclaimer-section';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileDownload } from '@fortawesome/pro-regular-svg-icons';
import { faFileDownload as faFileDownloadSolid, faArrowUp, faArrowDown } from '@fortawesome/pro-solid-svg-icons';
import { BloodworkTestAnalysisSection } from './bloodwork-test-analysis-section';
import { ImmuneInflammatoryBurdenPanel } from './immune-inflammatory-burden-panel';
import { MetabolicVulnerabilityInsights } from './metabolic-vulnerability-insights';
import { MvxResultsPanel } from './mvx-results-panel';
import { MvxBlueprintPanel } from './mvx-blueprint-panel';
import { MvxAboutSection } from './mvx-about-section';
import { MvxReferencesSection } from './mvx-references-section';
import { LABCORP_MVX_PLUS_URL } from '../../models/MetabolicVulnerability';
import { Footer } from '../../Footer';
import { RootCauseFrameworkPanel } from './root-cause-framework-panel';
import { RootCauseGuidanceTabs } from './root-cause-guidance-tabs';
import { ResultsOverviewPanel, hasResultsOverviewContent, isHdlHighSoleOutOfOptimal, isGlobulinOptimal, isImmuneAbsolute, keepInInflammationPanel, keepInImmunePanel, suppressAlpWhenZincSupersedes } from './results-overview-panel';
import { SupplementsSummaryPanel } from './supplements-summary-panel';
import { supplementsVisible } from '../../utils/supplementsVisibility';
import { SupplementProtocolsPanel, hasSupplementProtocolsContent } from './supplement-protocols-panel';
import { SectionNotesSummaryPanel } from './section-notes-summary-panel';
import { GenericModal } from '../common/generic-modal';
import { BloodworkEntryStore } from '../../stores/bloodwork-entry-store';
import { BloodworkNotes } from './bloodwork-notes';
import { SummaryDashboardSection } from './summary-dashboard-section';
import { buildSystemGroupDefs, buildAttrToSystem, buildSecondaryTagger, appendSecondaryLabels, groupBySystem as groupMarkersBySystem } from '../../models/mooSystemGrouping';
import { BMIBarChart } from '../charts/BMIBarChart';
import { Client, Gender } from '../../models';
import { MathUtils } from '../../utils';
import Select from 'react-select';
import { HeightOption, FT_OPTIONS, IN_OPTIONS, HEIGHT_SELECT_STYLES } from '../client/height-select-options';

const thumbs_up = require('../../assets/imgs/thumbs-up-15x15.png');
const thumbs_down = require('../../assets/imgs/thumbs-down-15x15.png');

// Collapse / Include-in-PDF key for the dev-only MVX section. Cast to Section so
// it reuses the existing toggle machinery without polluting the Section enum.
const MVX_SECTION = 'Metabolic Vulnerability (MVX Plus)' as unknown as Section;

type Props = {
  currentUserProfile: UserProfile;
  bloodwork: Bloodwork;
  previousBloodwork?: Bloodwork;
  bloodworkRanges: BloodworkRange[];
  furtherConsiderations: FurtherConsiderations;
  bloodworkEntryStore: BloodworkEntryStore;
  updateClient?: (client: Client) => Promise<Client>;
  currentClients?: Client[];
};

// Keys for the 6 "Markers Outside Optimal" sub-panels — used to track
// which panel(s) the user has collapsed (charts hidden) for the current session.
type OutsideOptimalPanelKey =
  | 'highAlarm'
  | 'lowAlarm'
  | 'aboveLab'
  | 'belowLab'
  | 'aboveOptimal'
  | 'belowOptimal';

const ALL_OUTSIDE_OPTIMAL_PANEL_KEYS: OutsideOptimalPanelKey[] = [
  'highAlarm', 'lowAlarm', 'aboveLab', 'belowLab', 'aboveOptimal', 'belowOptimal',
];

type State = {
  previousBloodwork: Bloodwork;
  collectionDate: string;
  bloodworkAnalysis: BloodworkAnalysis;
  showUpgradeModal: boolean;
  hideTestResultsAnalysis: boolean;
  collapsedSections: Section[];
  // Sections to exclude from the next PDF export. Seeded from the user profile
  // on mount and on bloodwork switch; toggled via the "Include in PDF" checkbox
  // in each section header. Session-only — never persists back to the profile.
  excludedFromPdfSections: Section[];
  editingClientInfo: boolean;
  pendingClient: Client | null;
  isSavingClientInfo: boolean;
  clientInfoValidationErrors: string[];
  // Per-session list of panels the user has collapsed (charts hidden).
  // Seeded on mount/bloodwork-switch from the user's
  // `markersOutsideOptimalDisplay` preference: 'showBars' → empty (all expanded),
  // 'hideBars' → all 6 keys (all collapsed). The user can flip any panel via its
  // chevron without persisting the change back to the profile.
  outsideOptimalChartsCollapsed: OutsideOptimalPanelKey[];
  // Session-only mirrors of the two Immune Section settings, exposed as quick
  // checkboxes in the Immune Activity section header so the user can flip them
  // without going back to Settings. Seeded on mount/bloodwork-switch from the
  // profile (`showOnlyImmuneSectionSignals`, `showImmuneAbsolutes`); toggling
  // them drives the on-screen Immune section (and its PDF export) but never
  // persists back to the profile — same pattern as "Include in PDF".
  showOnlyImmuneSectionSignals: boolean;
  showImmuneAbsolutes: boolean;
};

export class BloodworkTestAnalysis extends React.Component<Props, State> {
  state: State = {
    previousBloodwork: undefined,
    collectionDate: '',
    bloodworkAnalysis: undefined,
    showUpgradeModal: false,
    hideTestResultsAnalysis: false,
    collapsedSections: [],
    editingClientInfo: false,
    pendingClient: null,
    isSavingClientInfo: false,
    clientInfoValidationErrors: [],
    outsideOptimalChartsCollapsed: [],
    excludedFromPdfSections: [],
    showOnlyImmuneSectionSignals: true,
    showImmuneAbsolutes: true,
  };
  pdfWindow: Window;
  private previousBloodworkAnalysis?: BloodworkAnalysis;

  componentDidMount() {
    this.seedExcludedFromPdfFromProfile();
    this.seedOutsideOptimalCollapsedFromProfile();
    this.seedImmuneTogglesFromProfile();
    // Cross-section "See the … section" links inside Possible Root Causes text
    // (e.g. a viscosity input pointing at the HCT or protein section) fire this
    // window event instead of drilling a callback down through the tab tree.
    window.addEventListener(
      'labsmarts:navigate-section',
      this.handleSectionNav as EventListener,
    );
  }

  componentWillUnmount() {
    window.removeEventListener(
      'labsmarts:navigate-section',
      this.handleSectionNav as EventListener,
    );
  }

  handleSectionNav = (e: Event) => {
    const detail = (e as CustomEvent).detail || {};
    if (!detail.section) return;
    this.navigateToSection(detail.section as Section, detail.tab === 'causes');
  };

  componentWillReceiveProps(nextProps: Props) {
    console.log('BloodworkTestAnalysis.componentWillReceiveProps......');
    console.log(nextProps);
    const { bloodwork, previousBloodwork } = nextProps;
    const prevBloodworkId = this.props.bloodwork?.id;
    const nextBloodworkId = bloodwork?.id;
    const prevPreviousId = this.props.previousBloodwork?.id;
    const nextPreviousId = previousBloodwork?.id;
    if (nextBloodworkId !== prevBloodworkId) {
      // Switching bloodworks resets the per-panel "show/hide bars" toggles and
      // the per-test "Include in PDF" overrides back to whatever the user's
      // Settings preferences say.
      this.seedOutsideOptimalCollapsedFromProfile(nextProps.currentUserProfile);
      this.seedExcludedFromPdfFromProfile(nextProps.currentUserProfile);
      this.seedImmuneTogglesFromProfile(nextProps.currentUserProfile);
    }
    // When the parent clears the selected bloodwork (e.g. user clicked Edit
    // and we want the panel to disappear), drop the cached analysis so
    // render() returns null instead of leaving the prior panel visible.
    if (!bloodwork) {
      this.setState({ bloodworkAnalysis: undefined, previousBloodwork: undefined });
      return;
    }
    // Only re-analyze when the user switched to a different current or
    // comparison test. Saving the same bloodwork (e.g. editing the
    // Supplements override or Insights notes) shouldn't rebuild the analysis
    // or scroll the page back to the top of the Test Results Analysis panel.
    const switched =
      nextBloodworkId !== prevBloodworkId ||
      nextPreviousId !== prevPreviousId;
    // Cover the home-screen race where the user clicks Analyze on the first
    // test before bloodworkRanges have finished loading: the first analyze()
    // call exits without building an analysis, and every later props update
    // carries the same bloodwork id, so `switched` stays false and the panel
    // would remain blank until the user picked a *different* test. As long as a
    // test is selected, ranges are loaded, and we have no analysis yet, build
    // it. This is idempotent — `needsInitialAnalysis` is false once an analysis
    // exists, so unrelated prop updates (profile saves, re-saving the same
    // bloodwork) never rebuild it.
    const nextRanges = nextProps.bloodworkRanges;
    const rangesReady = !!nextRanges && nextRanges.length > 0;
    const needsInitialAnalysis = !this.state.bloodworkAnalysis;
    if (switched || (rangesReady && needsInitialAnalysis)) {
      // Pass nextProps' ranges — this.props.bloodworkRanges is still the stale
      // pre-update value while we're inside componentWillReceiveProps.
      this.analyze(bloodwork, previousBloodwork, nextRanges);
    }
  }

  private seedExcludedFromPdfFromProfile = (profile?: UserProfile) => {
    const p = profile ?? this.props.currentUserProfile;
    this.setState({
      excludedFromPdfSections: [...(p?.excludedFromPdfSections ?? [])],
    });
  };

  // Seed which Markers Outside Optimal panels start collapsed (charts hidden)
  // from the user's display-mode preference. 'showBars' → none collapsed,
  // 'hideBars' → all collapsed. ('hideSection' skips rendering entirely, so the
  // collapsed list is unused in that mode but we still default it to empty.)
  private seedOutsideOptimalCollapsedFromProfile = (profile?: UserProfile) => {
    const p = profile ?? this.props.currentUserProfile;
    const mode = p?.markersOutsideOptimalDisplay ?? 'showBars';
    this.setState({
      outsideOptimalChartsCollapsed:
        mode === 'hideBars' ? [...ALL_OUTSIDE_OPTIMAL_PANEL_KEYS] : [],
    });
  };

  // Seed the two Immune Section quick toggles from the user's saved
  // preferences. Both default to true when unset (matching the Settings page).
  private seedImmuneTogglesFromProfile = (profile?: UserProfile) => {
    const p = profile ?? this.props.currentUserProfile;
    this.setState({
      showOnlyImmuneSectionSignals: p?.showOnlyImmuneSectionSignals ?? true,
      showImmuneAbsolutes: p?.showImmuneAbsolutes ?? true,
    });
  };

  toggleShowOnlyImmuneSectionSignals = () => {
    this.setState(prev => ({ showOnlyImmuneSectionSignals: !prev.showOnlyImmuneSectionSignals }));
  };

  toggleShowImmuneAbsolutes = () => {
    this.setState(prev => ({ showImmuneAbsolutes: !prev.showImmuneAbsolutes }));
  };

  toggleIncludeInPdf = (section: Section) => {
    this.setState(prev => {
      const excluded = prev.excludedFromPdfSections.includes(section);
      return {
        excludedFromPdfSections: excluded
          ? prev.excludedFromPdfSections.filter(s => s !== section)
          : [...prev.excludedFromPdfSections, section],
      };
    });
  };

  private isIncludedInPdf = (section: Section): boolean =>
    !this.state.excludedFromPdfSections.includes(section);

  // `ranges` defaults to the current props, but callers inside
  // componentWillReceiveProps MUST pass nextProps.bloodworkRanges: at that
  // point this.props still holds the pre-update (often empty) ranges, so
  // reading them here would make the ranges-just-loaded recovery silently bail
  // and leave the analysis panel blank until the user picks a different test.
  analyze(
    bloodwork: Bloodwork,
    previousBloodwork: Bloodwork,
    ranges: BloodworkRange[] = this.props.bloodworkRanges,
  ) {
    const bloodworkRanges = ranges;
    if (bloodwork && bloodworkRanges && bloodworkRanges.length > 0) {
      const bloodworkAnalysis = new BloodworkAnalysis({
        bloodwork,
        bloodworkRanges,
        bloodworkFields: PanelField.ALL_FIELDS,
      });
      bloodworkAnalysis.init().performAnalysis();

      if (previousBloodwork) {
        const previousBloodworkAnalysis = new BloodworkAnalysis({
          bloodwork: previousBloodwork,
          bloodworkRanges,
          bloodworkFields: PanelField.ALL_FIELDS,
        });
        previousBloodworkAnalysis.init();
        bloodworkAnalysis.preparePreviousPanelMarkers(
          previousBloodworkAnalysis,
        );
        this.previousBloodworkAnalysis = previousBloodworkAnalysis;
      } else {
        this.previousBloodworkAnalysis = undefined;
      }

      this.setState(
        {
          bloodworkAnalysis,
          previousBloodwork: previousBloodwork
            ? new Bloodwork(previousBloodwork)
            : undefined,
        },
        // Scroll only after the panel has been committed to the DOM. Calling it
        // synchronously (as before) ran before the first render committed
        // #test-results-analysis-panel, leaving the scroll to a fragile 1s
        // setTimeout fallback — the panel would render but not come into view
        // on the first analyze.
        () => this.scrollToAnalyzeTestResults(),
      );
    }
  }

  startEditClientInfo = (): void => {
    const bloodwork = this.state.bloodworkAnalysis?.bloodwork;
    const client = bloodwork?.client;
    if (!client) return;
    const pending = new Client(client);
    // Default height/weight/units from the live client record when missing
    // on the snapshot — covers tests saved before height/weight were added
    // to the client profile. Fall back to bloodwork.clientId since older
    // snapshots may not carry id on the embedded client.
    const liveClientId = client.id ?? bloodwork?.clientId;
    const liveClient = liveClientId && this.props.currentClients
      ? Client.findClient(liveClientId, this.props.currentClients)
      : undefined;
    if (liveClient) {
      if (pending.height == null && liveClient.height != null) {
        pending.height = liveClient.height;
      }
      if (pending.weight == null && liveClient.weight != null) {
        pending.weight = liveClient.weight;
      }
      if (pending.units == null && liveClient.units != null) {
        pending.units = liveClient.units;
      }
    }
    this.setState({
      editingClientInfo: true,
      pendingClient: pending,
      clientInfoValidationErrors: [],
    });
  };

  cancelEditClientInfo = (): void => {
    this.setState({
      editingClientInfo: false,
      pendingClient: null,
      clientInfoValidationErrors: [],
    });
  };

  patchPendingClient = (apply: (client: Client) => void): void => {
    const { pendingClient } = this.state;
    if (!pendingClient) return;
    apply(pendingClient);
    this.forceUpdate();
  };

  private collectValidationMessages(
    pending: Client,
    classValidatorErrors: ValidationError[],
  ): string[] {
    const messages: string[] = [];
    const labels: Record<string, string> = {
      firstName: 'First Name',
      lastName: 'Last Name',
      gender: 'Birth Gender',
      dateOfBirth: 'Date of Birth',
      ethnicity: 'Ethnicity',
      menopause: 'Menopause/Pregnant',
      trimester: 'Trimester',
      elevation: 'Elevation',
      cigaretteSmoker: 'Cigarette Smoker',
      alcoholDrinker: 'Alcohol Drinker',
      height: 'Height',
      weight: 'Weight',
    };
    classValidatorErrors.forEach((err) => {
      const label = labels[err.property] ?? err.property;
      Object.keys(err.constraints || {}).forEach((c) => {
        messages.push(`${label} ${err.constraints[c]}`);
      });
    });
    if (pending.isTooYoungToBePregnant?.()) {
      messages.push('Date of Birth — client must be 15 years of age or older when Pregnant is selected.');
    }
    if (pending.isTooOld?.()) {
      messages.push('Date of Birth — client cannot be over 200 years of age.');
    }
    return messages;
  }

  saveClientInfo = async (): Promise<void> => {
    const { pendingClient, bloodworkAnalysis, previousBloodwork } = this.state;
    const bloodwork = bloodworkAnalysis?.bloodwork;
    if (!pendingClient || !bloodwork) return;

    this.setState({ isSavingClientInfo: true, clientInfoValidationErrors: [] });

    // Client-side validation first
    const classErrors = await validate(pendingClient);
    const messages = this.collectValidationMessages(pendingClient, classErrors);
    if (messages.length > 0) {
      this.setState({ clientInfoValidationErrors: messages, isSavingClientInfo: false });
      return;
    }

    const currentClient = bloodwork.client;
    const editableKeys: (keyof Client)[] = [
      'firstName', 'lastName', 'gender', 'dateOfBirth', 'ethnicity',
      'menopause', 'trimester', 'elevation', 'cigaretteSmoker', 'alcoholDrinker',
      'height', 'weight', 'units',
    ];
    try {
      editableKeys.forEach((k) => { (currentClient as any)[k] = (pendingClient as any)[k]; });
      // Persist height/weight on the test record itself — not only on the
      // embedded client snapshot. These are the canonical per-test fields the
      // data-entry path writes (bloodwork-entry.tsx) and that the BMI read
      // falls back to (`eff.height ?? bloodwork.height`). Storing them here
      // keeps this test's BMI even if the client profile's height/weight later
      // change or the embedded snapshot is re-derived. (Both are numbers in
      // canonical units: height in cm, weight in lbs.)
      bloodwork.height = pendingClient.height;
      bloodwork.weight = pendingClient.weight;
      if (this.props.updateClient) {
        await this.props.updateClient(currentClient);
      }
      // Also re-save the bloodwork so its embedded client snapshot persists
      // (otherwise the next load would show stale client values for this test).
      const savedBloodwork = await this.props.bloodworkEntryStore.saveBloodwork(bloodwork);
      if (!savedBloodwork) {
        throw new Error('Test results failed to save — please check your connection and try again.');
      }
      // Re-sync the in-memory client test list from the server. The analysis
      // works on a *clone* of the list entry (BloodworkAnalysis copies the
      // bloodwork via `new Bloodwork(...)`), so the entry the table re-selects
      // stays stale until reloaded. Without this, switching to another test and
      // back would drop the just-saved height/weight/BMI even though they were
      // persisted. Mirrors the data-entry path (bloodwork-entry.tsx).
      if (bloodwork.clientId && this.props.bloodworkEntryStore.loadClientBloodworks) {
        this.props.bloodworkEntryStore
          .loadClientBloodworks(bloodwork.clientId)
          .catch((e) => console.error('Failed to refresh client bloodworks after saving client info', e));
      }
      // Re-run analysis since reference ranges depend on age/sex/etc.
      this.analyze(bloodwork, previousBloodwork);
      this.setState({
        editingClientInfo: false,
        pendingClient: null,
        clientInfoValidationErrors: [],
      });
    } catch (err) {
      console.error('Failed to save client info', err);
      const detail = (err && (err.response?.data?.message || err.message || JSON.stringify(err))) || 'Unknown error';
      this.setState({
        clientInfoValidationErrors: [`Server error: ${detail}`],
      });
    } finally {
      this.setState({ isSavingClientInfo: false });
    }
  };

  scrollToAnalyzeTestResults() {
    const scrollToPanel = () => {
      const element = document.getElementById('test-results-analysis-panel');
      if (!element) return false;
      // Offset by the sticky header height so the panel title lands at the
      // very top of the visible page instead of hiding behind the navbar.
      const sticky = document.querySelector('.App-sticky-top') as HTMLElement | null;
      const stickyHeight = sticky ? sticky.getBoundingClientRect().height : 0;
      const top = element.getBoundingClientRect().top + window.scrollY - stickyHeight;
      window.scrollTo({ top });
      return true;
    };
    if (!scrollToPanel()) {
      // Hack to resolve issue with scrolling down the first time.
      setTimeout(scrollToPanel, 1000);
    }
  }

  export = (withNotes = false) => {
    console.log('export button clicked.....');
    const { currentUserProfile } = this.props;
    const { bloodworkAnalysis, previousBloodwork, excludedFromPdfSections, outsideOptimalChartsCollapsed } = this.state;

    if (!currentUserProfile.pdfExportAllowed) {
      this.setState({ showUpgradeModal: true });
      return;
    }

    sessionStorage.setItem(
      'bloodwork',
      JSON.stringify(bloodworkAnalysis.bloodwork),
    );
    if (previousBloodwork) {
      sessionStorage.setItem(
        'previousBloodwork',
        JSON.stringify(previousBloodwork),
      );
    } else {
      sessionStorage.removeItem('previousBloodwork');
    }
    if (withNotes) {
      sessionStorage.setItem('exportWithNotes', 'true');
    } else {
      sessionStorage.removeItem('exportWithNotes');
    }
    // PDF inclusion: a section is excluded iff its "Include in PDF" checkbox
    // is unchecked at click time. UI collapse state no longer affects PDF.
    sessionStorage.setItem(
      'excludedFromPdfSections',
      JSON.stringify(excludedFromPdfSections),
    );
    // Mirror the on-screen "Show bar graphs" checkbox into the PDF: if any
    // MOO sub-panel is collapsed at export time, the PDF renders without bars.
    // Lets the user flip bars on/off per-export without touching the saved
    // `markersOutsideOptimalDisplay` profile preference.
    sessionStorage.setItem(
      'showBarsInMoo',
      JSON.stringify(outsideOptimalChartsCollapsed.length === 0),
    );
    sessionStorage.setItem(
      'showOnlySectionSignals',
      JSON.stringify(!!currentUserProfile.showOnlySectionSignals),
    );
    // Mirror the Immune Activity section's on-screen quick toggles into the
    // PDF so the export matches what the user is looking at, without touching
    // the saved profile preferences.
    sessionStorage.setItem(
      'showOnlyImmuneSectionSignals',
      JSON.stringify(this.state.showOnlyImmuneSectionSignals),
    );
    sessionStorage.setItem(
      'showImmuneAbsolutes',
      JSON.stringify(this.state.showImmuneAbsolutes),
    );
    sessionStorage.setItem(
      'showPreviousTestMarkersInComparison',
      JSON.stringify(currentUserProfile.showPreviousTestMarkersInComparison ?? true),
    );

    if (this.pdfWindow) {
      this.pdfWindow.close();
    }
    this.pdfWindow = window.open(
      '/bloodwork/analyze/pdf',
      'targetWindow',
      'popup=yes,toolbar=no,location=no,status=no,menubar=no,scrollbars=no,resizable=no,width=1028,height=764',
    );
  };

  exportTooltip() {
    return (
      <Tooltip id="export-tooltip">
        <div>Click this icon to export to PDF</div>
      </Tooltip>
    );
  }

  exportWithNotesTooltip() {
    return (
      <Tooltip id="export-with-notes-tooltip">
        <div>Click this icon to export to PDF with notes</div>
      </Tooltip>
    );
  }

  exportInfoTooltip() {
    return (
      <Tooltip id="export-info-tooltip">
        <div>
          Click this icon to learn more about the export to PDF feature.
        </div>
      </Tooltip>
    );
  }

  openWindow = () => {
    window.open('https://labsmarts.com/testresultsanalysis');
  };

  openExportInfoWindow = () => {
    window.open('https://labsmarts.com/exporttopdf');
  };

  toggleSection = (section: Section) => {
    this.setState(prev => {
      const already = prev.collapsedSections.includes(section);
      return {
        collapsedSections: already
          ? prev.collapsedSections.filter(s => s !== section)
          : [...prev.collapsedSections, section],
      };
    });
  };

  // Expand (if collapsed) and smooth-scroll to a section. Shared by the Results
  // Overview buttons and the Supplement Protocols section links.
  jumpToSection = (section: Section) => {
    const scrollToSection = () => {
      const element = document.getElementById(`section-${section}`);
      if (!element) return;
      const sticky = document.querySelector('.App-sticky-top') as HTMLElement | null;
      const stickyHeight = sticky ? sticky.getBoundingClientRect().height : 0;
      const top = element.getBoundingClientRect().top + window.scrollY - stickyHeight;
      window.scrollTo({ top, behavior: 'smooth' });
    };
    if (this.state.collapsedSections.includes(section)) {
      this.toggleSection(section);
      // Wait for the expand to flush to the DOM so the scroll target's final Y
      // position is what we measure.
      requestAnimationFrame(() => requestAnimationFrame(scrollToSection));
    } else {
      scrollToSection();
    }
  };

  // Like jumpToSection, but for the cross-reference links inside Possible Root
  // Causes text. Expands (if collapsed) and scrolls to the target section, and
  // when openCausesTab is set, activates that section's "Possible Root Causes"
  // tab. The section Tabs are uncontrolled react-bootstrap Tabs, so we open the
  // tab by clicking its nav link — scoped to the destination's #section-<X>
  // container so a sibling section's identically-named tab isn't touched.
  navigateToSection = (section: Section, openCausesTab: boolean) => {
    const run = () => {
      const element = document.getElementById(`section-${section}`);
      if (!element) return;
      if (openCausesTab) {
        const tab = Array.from(
          element.querySelectorAll('[role="tab"], a'),
        ).find(
          el => (el.textContent || '').trim() === 'Possible Root Causes',
        ) as HTMLElement | undefined;
        if (tab) tab.click();
      }
      // The section's top edge doesn't move when its inner tab content changes,
      // so measuring after the tab click is safe.
      const sticky = document.querySelector('.App-sticky-top') as HTMLElement | null;
      const stickyHeight = sticky ? sticky.getBoundingClientRect().height : 0;
      const top = element.getBoundingClientRect().top + window.scrollY - stickyHeight;
      window.scrollTo({ top, behavior: 'smooth' });
    };
    if (this.state.collapsedSections.includes(section)) {
      this.toggleSection(section);
      requestAnimationFrame(() => requestAnimationFrame(run));
    } else {
      requestAnimationFrame(run);
    }
  };

  toggleOutsideOptimalCharts = (panel: OutsideOptimalPanelKey) => {
    this.setState(prev => {
      const collapsed = prev.outsideOptimalChartsCollapsed.includes(panel);
      return {
        outsideOptimalChartsCollapsed: collapsed
          ? prev.outsideOptimalChartsCollapsed.filter(p => p !== panel)
          : [...prev.outsideOptimalChartsCollapsed, panel],
      };
    });
  };

  // Section-header "Show bar graphs" checkbox: expand-all when currently any
  // panel is collapsed, collapse-all when every panel is already expanded.
  toggleAllOutsideOptimalCharts = () => {
    this.setState(prev => ({
      outsideOptimalChartsCollapsed:
        prev.outsideOptimalChartsCollapsed.length === 0
          ? [...ALL_OUTSIDE_OPTIMAL_PANEL_KEYS]
          : [],
    }));
  };

  toggleTestResultsAnalysis() {
    this.setState({
      hideTestResultsAnalysis: !this.state.hideTestResultsAnalysis,
    });
  }

  private renderSectionSummary() {
    const { bloodworkAnalysis, hideTestResultsAnalysis } = this.state;
    if (!bloodworkAnalysis || hideTestResultsAnalysis) return null;

    const wbc = (bloodworkAnalysis as any).wbc;
    const hasWbcValue = wbc?.hasValue?.() === true;

    // Hide the entire Results Overview card when there are no markers to
    // summarize (e.g. a test with no blood values entered yet). Without this
    // the "Results Overview" header would render above an empty panel body.
    if (!hasResultsOverviewContent(
      bloodworkAnalysis,
      this.previousBloodworkAnalysis,
      hasWbcValue,
      this.props.currentUserProfile.showOnlySectionSignals,
      this.state.showOnlyImmuneSectionSignals,
    )) {
      return null;
    }

    const colorLegendTooltip = (
      // Wide on purpose: this legend has grown long enough that a narrow column
      // ran taller than the viewport and clipped off the top (Popper can shift a
      // popover, but it can't shrink one that doesn't fit). Trading width for
      // height keeps it on screen; the vw cap stops it overflowing narrow
      // windows, and the maxHeight scrolls rather than clips if it ever
      // outgrows the viewport again.
      <Popover
        id="results-overview-popover"
        style={{ maxWidth: 'min(760px, 92vw)', width: 'max-content' }}
      >
        <Popover.Content style={{ maxHeight: '70vh', overflowY: 'auto' }}>
        <div style={{ textAlign: 'left' }}>
          <strong>Each section's status reflects its most concerning marker:</strong>
          <br />
          <span style={{ color: '#5CB166' }}>&#9632;</span> <strong>Optimal</strong> — all markers within their optimal range
          <br />
          <span style={{ color: '#D8BE47' }}>&#9632;</span> <strong>Monitor</strong> — at least one marker outside optimal but within lab range
          <br />
          <span style={{ color: '#E1974B' }}>&#9632;</span> <strong>Support</strong> — at least one marker outside lab range but not in alarm range
          <br />
          <span style={{ color: '#E56A5A' }}>&#9632;</span> <strong>Priority</strong> — at least one marker in the alarm range
          <br /><br />
          {/* Paired with showTopAreas in results-overview-panel.tsx — the band
              is hidden, so this paragraph stays out of the legend. Restore both
              together, or the legend documents an element that isn't rendered.
          <strong>Top Areas to Review</strong> at the top of the panel gathers the most concerning sections from across all five groups, ranked by the same rules described below. Only <strong>Priority</strong> and <strong>Support</strong> sections qualify — it lists three, or five when more than three sections are Priority.
          <br /><br />
          */}
          The <strong>four circles at the top of each system section</strong> count how many of that system's <strong>sections</strong> fall into each status (Optimal / Monitor / Support / Priority).
          <br /><br />
          The <strong>thin bar under each section</strong> shows how that section's individual <strong>markers</strong> split across the same four statuses, each segment sized by how many markers fall in it, with the counts below and the section total beside it.
          <br /><br />
          Within each group, sections are sorted by status — most concerning first — and sections sharing a status are ordered by <strong>how many markers sit outside optimal</strong>, so a section with nine markers slightly off sits above one with three in the alarm range.
          <br /><br />
          A <strong>&#128077; thumbs up</strong> next to a section means it improved compared to the previous results. A <strong>&#128078; thumbs down</strong> means it worsened.
          <br /><br />
          Click any section to jump to it; the red arrow button &#8679; in the bottom-right corner brings you back here at any time.
        </div>
        </Popover.Content>
      </Popover>
    );

    const resultsOverviewCollapsed = this.state.collapsedSections.includes(Section.RESULTS_OVERVIEW);

    return (
      <Card className="mb-3" id="results-overview">
        <Card.Header
          onClick={() => this.toggleSection(Section.RESULTS_OVERVIEW)}
          style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
        >
          <span>
            <FontAwesomeIcon icon={resultsOverviewCollapsed ? 'chevron-down' : 'chevron-up'} />
            &nbsp;&nbsp;Results Overview&nbsp;&nbsp;
            <span onClick={e => e.stopPropagation()}>
              {/* "right-start" (not "right") so the popover's TOP edge aligns
                  with the info icon and it grows downward. Plain "right" centers
                  it on the icon, which sent half of this tall legend above the
                  viewport where it was clipped with no way to scroll to it. */}
              <OverlayTrigger overlay={colorLegendTooltip} placement="right-start" trigger="click" rootClose>
                <FontAwesomeIcon icon="info-circle" style={{ color: '#fff', cursor: 'pointer' }} />
              </OverlayTrigger>
            </span>
          </span>
          <label
            htmlFor="include-in-pdf-RESULTS_OVERVIEW"
            onClick={e => e.stopPropagation()}
            style={{ display: 'inline-flex', alignItems: 'center', margin: 0, fontWeight: 'normal', cursor: 'pointer', color: '#fff' }}
            title="Include this section in the Analysis PDF"
          >
            <span style={{ marginRight: '6px' }}>Include in PDF</span>
            <input
              id="include-in-pdf-RESULTS_OVERVIEW"
              type="checkbox"
              className="lsm-styled-checkbox"
              checked={this.isIncludedInPdf(Section.RESULTS_OVERVIEW)}
              onChange={() => this.toggleIncludeInPdf(Section.RESULTS_OVERVIEW)}
            />
          </label>
        </Card.Header>
        {!resultsOverviewCollapsed && (
          <Card.Body>
            <ResultsOverviewPanel
              bloodworkAnalysis={bloodworkAnalysis}
              previousBloodworkAnalysis={this.previousBloodworkAnalysis}
              hasWbcValue={hasWbcValue}
              direction="row"
              showOnlySectionSignals={this.props.currentUserProfile.showOnlySectionSignals}
              showOnlyImmuneSectionSignals={this.state.showOnlyImmuneSectionSignals}
              showImmuneAbsolutes={this.state.showImmuneAbsolutes}
              onSectionClick={this.jumpToSection}
            />
          </Card.Body>
        )}
      </Card>
    );
  }

  render() {
    console.log('BloodworkTestAnalysis.render......');
    const {
      bloodworkAnalysis,
      showUpgradeModal,
      hideTestResultsAnalysis,
      previousBloodwork,
      collapsedSections,
    } = this.state;
    const { furtherConsiderations, bloodworkEntryStore } = this.props;

    if (!bloodworkAnalysis) {
      return null;
    }

    const isCollapsed = (section: Section) => collapsedSections.includes(section);

    const wbc = (bloodworkAnalysis as any).wbc;
    const hasWbcValue = wbc?.hasValue?.() === true;

    // When comparing to a previous test, optionally append "previous-only"
    // markers to a section's reference-range charts: markers that had a value
    // last time but not now, plotted with just their previous (blue) blood
    // drop. Gated on the practitioner's setting and on the presence of a
    // previous test. We only augment sections that already have at least one
    // current marker — a section with no current results doesn't render (and
    // its Results Overview button is hidden too), so we don't resurrect it here.
    const showPreviousOnly =
      (this.props.currentUserProfile.showPreviousTestMarkersInComparison ?? true) &&
      !!this.previousBloodworkAnalysis;
    const withPreviousOnly = (
      currentMarkers: PanelMarker[],
      getPrev: (a: BloodworkAnalysis) => PanelMarker[],
    ): PanelMarker[] => {
      if (!showPreviousOnly || currentMarkers.length === 0) {
        return currentMarkers;
      }
      const previousOnly = bloodworkAnalysis.getPreviousOnlyPanelMarkers(
        currentMarkers,
        getPrev(this.previousBloodworkAnalysis!),
      );
      return previousOnly.length
        ? [...currentMarkers, ...previousOnly]
        : currentMarkers;
    };

    // Build Above/Below Optimal marker lists (same logic as the PDF view)
    const allMarkerSet = new Set<PanelMarker>([
      ...bloodworkAnalysis.getRBCPanelMarkers(),
      ...bloodworkAnalysis.getIronPanelMarkers(),
      ...bloodworkAnalysis.getWBCPanelMarkers(),
      ...bloodworkAnalysis.getPlateletPanelMarkers(),
      ...bloodworkAnalysis.getGlucosePanelMarkers(),
      ...bloodworkAnalysis.getKidneyPanelMarkers(),
      ...bloodworkAnalysis.getProteinsPanelMarkers(),
      ...bloodworkAnalysis.getMineralPanelMarkers(),
      ...bloodworkAnalysis.getElectrolytePanelMarkers(),
      ...bloodworkAnalysis.getLiverPanelMarkers(),
      ...bloodworkAnalysis.getLipidsPanelMarkers(),
      ...bloodworkAnalysis.getInflammationOxidativeStressPanelMarkers(),
      ...bloodworkAnalysis.getThyroidPanelMarkers(),
      ...bloodworkAnalysis.getStressAdrenalPanelMarkers(),
      ...bloodworkAnalysis.getSexHormonePanelMarkers(),
      ...bloodworkAnalysis.getProstatePanelMarkers(),
    ]);
    const allMarkers = Array.from(allMarkerSet);

    const isHigherIsBetter = (pm: PanelMarker) =>
      pm.isEGFR?.() || pm.field?.useEGFRChart;

    const isRdwChart = (pm: PanelMarker) => pm.field?.useRDWChart;

    const egfrBelowTier = (pm: PanelMarker): 1 | 2 | 3 => {
      const r = pm.range;
      if (!r) return 3;
      if (r.alarmMin > 0 && pm.value < r.alarmMin) return 1;
      if (r.labMin > 0 && pm.value < r.labMin) return 2;
      return 3;
    };

    // Zone-penetration score: fraction of the relevant danger zone consumed.
    // WBC uses its own 9-zone percentFromMean which is already zone-normalized.
    // Falls back to |percentFromMean| when denominators collapse to zero.
    const zonePenetrationScore = (pm: PanelMarker, section: 'alarm' | 'lab' | 'optimal'): number => {
      const r = pm.range;
      if (!r) return Math.abs(pm.percentFromMean ?? 0);
      if (pm.field?.useWBCPercentFromMean) return Math.abs(pm.percentFromMean ?? 0);
      const v = pm.value;
      if (section === 'alarm') {
        const denom = r.stdDev && r.stdDev > 0 ? r.stdDev : 1;
        if (v > r.alarmMax) return (v - r.alarmMax) / denom;
        if (r.alarmMin > 0 && v < r.alarmMin) return (r.alarmMin - v) / denom;
        return 0;
      }
      if (section === 'lab') {
        if (v > r.labMax) {
          const denom = r.alarmMax - r.labMax;
          return denom > 0 ? (v - r.labMax) / denom : Math.abs(pm.percentFromMean ?? 0);
        }
        const denom = r.labMin - (r.alarmMin ?? 0);
        return denom > 0 ? (r.labMin - v) / denom : Math.abs(pm.percentFromMean ?? 0);
      }
      // section === 'optimal'
      if (v > (r.optimalMax ?? r.labMax)) {
        const denom = r.labMax - (r.optimalMax ?? r.labMax);
        return denom > 0 ? (v - r.optimalMax!) / denom : Math.abs(pm.percentFromMean ?? 0);
      }
      const optMin = r.optimalMin ?? r.labMin;
      const denomOpt = optMin - r.labMin;
      return denomOpt > 0 ? (optMin - v) / denomOpt : Math.abs(pm.percentFromMean ?? 0);
    };

    const alarmZoneDesc   = (a: PanelMarker, b: PanelMarker) => zonePenetrationScore(b, 'alarm')   - zonePenetrationScore(a, 'alarm');
    const labZoneDesc     = (a: PanelMarker, b: PanelMarker) => zonePenetrationScore(b, 'lab')     - zonePenetrationScore(a, 'lab');
    const optimalZoneDesc = (a: PanelMarker, b: PanelMarker) => zonePenetrationScore(b, 'optimal') - zonePenetrationScore(a, 'optimal');

    // "Alarm Range Markers" — outside alarm threshold in either direction, worst first
    const needsAttentionGroup = allMarkers
      .filter(pm => pm.hasValue() && (
        isHigherIsBetter(pm)
          ? pm.isStricklyLessThanOptimal?.() && egfrBelowTier(pm) === 1
          : (pm.isGreaterThanAlarm?.() || (!isRdwChart(pm) && pm.isLessThanAlarm?.()))
      ))
      .sort(alarmZoneDesc);

    // "Outside Lab Range Markers" — outside lab range but within alarm, worst first
    const worthDiscussingGroup = allMarkers
      .filter(pm => pm.hasValue() && (
        isHigherIsBetter(pm)
          ? pm.isStricklyLessThanOptimal?.() && egfrBelowTier(pm) === 2
          : (
              (pm.isGreaterThanLab?.() && !pm.isGreaterThanAlarm?.()) ||
              (!isRdwChart(pm) && pm.isLessThanLab?.() && !pm.isLessThanAlarm?.())
            )
      ))
      .sort(labZoneDesc);

    // "Outside Optimal Range Markers" — outside optimal but within lab range, worst first
    const fineTuneGroup = allMarkers
      .filter(pm => pm.hasValue() && (
        isHigherIsBetter(pm)
          ? pm.isStricklyLessThanOptimal?.() && egfrBelowTier(pm) === 3
          : (
              (pm.isGreaterThanOptimal?.() && !pm.isGreaterThanLab?.()) ||
              (!isRdwChart(pm) && pm.isStricklyLessThanOptimal?.() && !pm.isLessThanLab?.())
            )
      ))
      .sort(optimalZoneDesc);

    // Direction-split groups for the "Markers Outside Optimal" 6-panel layout.
    // Derived from the 3 zone groups above so all EGFR/RDW/higher-is-better
    // edge cases are inherited unchanged. EGFR markers (higher-is-better) only
    // ever flag when low, so they naturally land in the low-side panels.
    // RDW markers only flag high, so they only land in the high-side panels.
    const isHighSide = (pm: PanelMarker) =>
      !isHigherIsBetter(pm) &&
      (pm.isGreaterThanAlarm?.() || pm.isGreaterThanLab?.() || pm.isGreaterThanOptimal?.());

    const highAlarmGroup    = needsAttentionGroup.filter(isHighSide);
    const lowAlarmGroup     = needsAttentionGroup.filter(pm => !isHighSide(pm));
    const aboveLabGroup     = worthDiscussingGroup.filter(isHighSide);
    const belowLabGroup     = worthDiscussingGroup.filter(pm => !isHighSide(pm));
    const aboveOptimalGroup = fineTuneGroup.filter(isHighSide);
    const belowOptimalGroup = fineTuneGroup.filter(pm => !isHighSide(pm));

    // System grouping for "Markers Outside Optimal" lives in the shared
    // mooSystemGrouping module so the web view and the PDF export stay in sync.
    // Markers group by their primary home; cross-cutting relevance (Inflammation /
    // Glucose) is shown per-marker via the secondary tagger.
    const mooSystemDefs = buildSystemGroupDefs(bloodworkAnalysis);
    const attrToSystem = buildAttrToSystem(mooSystemDefs);
    const secondaryTags = buildSecondaryTagger(mooSystemDefs);
    const groupBySystem = (
      markers: PanelMarker[],
      scoreOf: (pm: PanelMarker) => number,
    ) => appendSecondaryLabels(groupMarkersBySystem(markers, scoreOf, attrToSystem), secondaryTags);

    const collectionDate = bloodworkAnalysis.bloodwork.collectionDate;
    const client = bloodworkAnalysis.bloodwork.client;
    const panelTooltip = (
      <Tooltip id="modal-tooltip">
        Click this icon to learn more about the items in this panel.
      </Tooltip>
    );

    const evalLarge = 3;
    const evalSmall = 12;

    const refLarge = 9;
    const refSmall = 12;

    const hideTestResultsAnalysisIconName = hideTestResultsAnalysis
      ? 'chevron-down'
      : 'chevron-up';

    return (
      <>
        <Card id="test-results-analysis-panel">
          <Card.Header>
            <FontAwesomeIcon
              icon={hideTestResultsAnalysisIconName}
              onClick={() => this.toggleTestResultsAnalysis()}
            />
            &nbsp;&nbsp;Test Results Analysis&nbsp;
            <OverlayTrigger overlay={panelTooltip}>
              <FontAwesomeIcon
                icon="external-link-alt"
                onClick={this.openWindow}
              />
            </OverlayTrigger>
            <span className="float-right">
              <OverlayTrigger
                overlay={this.exportInfoTooltip()}
                placement="bottom"
              >
                <FontAwesomeIcon
                  icon="info-circle"
                  onClick={this.openExportInfoWindow}
                />
              </OverlayTrigger>
              &nbsp;&nbsp;&nbsp;
              <OverlayTrigger overlay={this.exportTooltip()} placement="bottom">
                <FontAwesomeIcon
                  icon={faFileDownload}
                  onClick={() => this.export(false)}
                />
              </OverlayTrigger>
              &nbsp;&nbsp;&nbsp;
              <OverlayTrigger
                overlay={this.exportWithNotesTooltip()}
                placement="bottom"
              >
                <FontAwesomeIcon
                  icon={faFileDownloadSolid}
                  onClick={() => this.export(true)}
                />
              </OverlayTrigger>
            </span>
          </Card.Header>
          {hideTestResultsAnalysis ? null : (
            <Card.Body>
              <Card>
                <Card.Header style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span>Client Information</span>
                  <label
                    htmlFor="include-in-pdf-BMI"
                    style={{ display: 'inline-flex', alignItems: 'center', margin: 0, fontWeight: 'normal', cursor: 'pointer', color: '#fff' }}
                    title="Include the BMI chart in the Analysis PDF"
                  >
                    <span style={{ marginRight: '6px' }}>Include BMI in PDF</span>
                    <input
                      id="include-in-pdf-BMI"
                      type="checkbox"
                      className="lsm-styled-checkbox"
                      checked={this.isIncludedInPdf(Section.BMI)}
                      onChange={() => this.toggleIncludeInPdf(Section.BMI)}
                    />
                  </label>
                </Card.Header>
                <Card.Body>
                  <div>
                    {this.state.editingClientInfo && this.state.clientInfoValidationErrors.length > 0 && (
                      <div className="alert alert-danger" style={{ padding: '8px 12px', marginBottom: 12, fontSize: 13 }}>
                        <strong>Please fix the following before saving:</strong>
                        <ul style={{ marginBottom: 0, paddingLeft: 20, marginTop: 4 }}>
                          {this.state.clientInfoValidationErrors.map((m, i) => (
                            <li key={i}>{m}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {(() => {
                      const editing = this.state.editingClientInfo;
                      const eff: Client = (editing && this.state.pendingClient) ? this.state.pendingClient : client;
                      const isFemale = eff.gender === Gender.FEMALE;
                      const isPregnant = isFemale && eff.menopause === Menopause.PREGNANT;
                      const units: 'US' | 'SI' = eff.units === 'SI' ? 'SI' : 'US';

                      const bw = bloodworkAnalysis.bloodwork as any;
                      const heightCm = eff.height != null ? Number(eff.height) : (bw.height != null ? Number(bw.height) : null);
                      const weightLbs = eff.weight != null ? Number(eff.weight) : (bw.weight != null ? Number(bw.weight) : null);
                      const bmiNumber = heightCm != null && weightLbs != null
                        ? MathUtils.bmi(weightLbs, heightCm)
                        : null;
                      const bmiSex: 'M' | 'F' | undefined = eff.gender === Gender.MALE
                        ? 'M'
                        : eff.gender === Gender.FEMALE
                          ? 'F'
                          : undefined;

                      const patch = this.patchPendingClient;
                      const editableCellStyle: React.CSSProperties = editing
                        ? { backgroundColor: '#fff', verticalAlign: 'middle' }
                        : { verticalAlign: 'middle' };
                      const inputStyle: React.CSSProperties = {
                        width: '100%',
                        minWidth: 70,
                        fontSize: 'inherit',
                        padding: '3px 5px',
                        border: '1px solid #ced4da',
                        borderRadius: 3,
                        outline: 'none',
                        backgroundColor: '#fff',
                      };
                      const smallInputStyle: React.CSSProperties = { ...inputStyle, minWidth: 0, width: 48 };

                      const textTd = (val: string, onChange: (v: string) => void) => (
                        <td style={editableCellStyle}>
                          {editing
                            ? <input type="text" maxLength={50} value={val ?? ''}
                                onChange={(e) => onChange(e.target.value)} style={inputStyle} />
                            : (val || <span style={{ color: '#adb5bd' }}>—</span>)
                          }
                        </td>
                      );

                      const selectTd = (
                        val: string,
                        opts: { value: string; label: string }[],
                        display: React.ReactNode,
                        onChange: (v: string) => void,
                      ) => (
                        <td style={editableCellStyle}>
                          {editing
                            ? <select value={val ?? ''} onChange={(e) => onChange(e.target.value)} style={inputStyle}>
                                {opts.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                              </select>
                            : (display ?? <span style={{ color: '#adb5bd' }}>—</span>)
                          }
                        </td>
                      );

                      const dateTd = (
                        isoVal: string | undefined,
                        display: React.ReactNode,
                        onChange: (iso: string | undefined) => void,
                      ) => (
                        <td style={editableCellStyle}>
                          {editing
                            ? <DatePicker
                                selected={isoVal ? moment(isoVal).toDate() : null}
                                onChange={(d: Date | null) =>
                                  onChange(d ? moment(d).startOf('day').toISOString() : undefined)}
                                maxDate={new Date()}
                                peekNextMonth
                                showMonthDropdown
                                showYearDropdown
                                dropdownMode="select"
                                dateFormat="MM/dd/yyyy"
                                customInput={<input style={inputStyle} />}
                              />
                            : (display ?? <span style={{ color: '#adb5bd' }}>—</span>)
                          }
                        </td>
                      );

                      const numberTd = (
                        val: number | undefined,
                        display: React.ReactNode,
                        onChange: (v: number | undefined) => void,
                        min?: number,
                        max?: number,
                        suffix?: string,
                      ) => (
                        <td style={editableCellStyle}>
                          {editing ? (
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                              <input
                                type="number" min={min} max={max}
                                value={val ?? ''}
                                onChange={(e) => onChange(e.target.value === '' ? undefined : Number(e.target.value))}
                                style={smallInputStyle}
                              />
                              {suffix && <span style={{ fontSize: 11, color: '#666' }}>{suffix}</span>}
                            </span>
                          ) : (display ?? <span style={{ color: '#adb5bd' }}>—</span>)}
                        </td>
                      );

                      const booleanTd = (
                        val: boolean,
                        display: React.ReactNode,
                        onChange: (v: boolean) => void,
                      ) => (
                        <td style={editableCellStyle}>
                          {editing
                            ? <select value={val ? 'yes' : 'no'} onChange={(e) => onChange(e.target.value === 'yes')} style={inputStyle}>
                                <option value="no">No</option>
                                <option value="yes">Yes</option>
                              </select>
                            : display
                          }
                        </td>
                      );

                      const heightTd = () => {
                        if (!editing) {
                          return <td style={editableCellStyle}>{eff.height != null ? client.displayHeight : <span style={{ color: '#adb5bd' }}>—</span>}</td>;
                        }
                        if (units === 'US') {
                          const { ft, inches } = eff.height != null
                            ? MathUtils.cmToFtIn(eff.height)
                            : { ft: '' as any, inches: '' as any };
                          const ftStr = ft === '' ? '' : String(ft);
                          const inStr = inches === '' ? '' : String(inches);
                          return (
                            <td style={editableCellStyle}>
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                                <div style={{ width: 70 }}>
                                  <Select<HeightOption>
                                    isSearchable={false}
                                    maxMenuHeight={400}
                                    options={FT_OPTIONS}
                                    styles={HEIGHT_SELECT_STYLES}
                                    value={FT_OPTIONS.find(o => o.value === ftStr) ?? FT_OPTIONS[0]}
                                    onChange={(opt) => {
                                      const v = opt?.value ?? '';
                                      const newFt = v === '' ? 0 : Number(v);
                                      const curIn = eff.height != null ? MathUtils.cmToFtIn(eff.height).inches : 0;
                                      patch((c) => { c.height = v === '' && (inches === 0 || inches === '')
                                        ? undefined
                                        : MathUtils.ftInToCm(newFt, Number(curIn) || 0); });
                                    }}
                                  />
                                </div>
                                <span style={{ fontSize: 11, color: '#666' }}>ft</span>
                                <div style={{ width: 70 }}>
                                  <Select<HeightOption>
                                    isSearchable={false}
                                    maxMenuHeight={400}
                                    options={IN_OPTIONS}
                                    styles={HEIGHT_SELECT_STYLES}
                                    value={IN_OPTIONS.find(o => o.value === inStr) ?? IN_OPTIONS[0]}
                                    onChange={(opt) => {
                                      const v = opt?.value ?? '';
                                      const newIn = v === '' ? 0 : Number(v);
                                      const curFt = eff.height != null ? MathUtils.cmToFtIn(eff.height).ft : 0;
                                      patch((c) => { c.height = v === '' && (ft === 0 || ft === '')
                                        ? undefined
                                        : MathUtils.ftInToCm(Number(curFt) || 0, newIn); });
                                    }}
                                  />
                                </div>
                                <span style={{ fontSize: 11, color: '#666' }}>in</span>
                              </span>
                            </td>
                          );
                        }
                        return (
                          <td style={editableCellStyle}>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                              <input type="number" min={0}
                                value={eff.height ?? ''}
                                onChange={(e) => patch((c) => { c.height = e.target.value === '' ? undefined : Number(e.target.value); })}
                                style={{ ...smallInputStyle, width: 58 }} />
                              <span style={{ fontSize: 11, color: '#666' }}>cm</span>
                            </span>
                          </td>
                        );
                      };

                      const weightTd = () => {
                        if (!editing) {
                          return <td style={editableCellStyle}>{eff.weight != null ? client.displayWeight : <span style={{ color: '#adb5bd' }}>—</span>}</td>;
                        }
                        const displayed = eff.weight == null
                          ? ''
                          : units === 'SI' ? MathUtils.lbsToKg(eff.weight) : eff.weight;
                        return (
                          <td style={editableCellStyle}>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                              <input type="number" min={0} max={units === 'SI' ? 453 : 999}
                                value={displayed === '' ? '' : displayed}
                                onChange={(e) => {
                                  const raw = e.target.value;
                                  if (raw === '') { patch((c) => { c.weight = undefined; }); return; }
                                  const n = Number(raw);
                                  if (isNaN(n)) return;
                                  patch((c) => { c.weight = units === 'SI' ? MathUtils.kgToLbs(n) : n; });
                                }}
                                style={{ ...smallInputStyle, width: 58 }} />
                              <span style={{ fontSize: 11, color: '#666' }}>{units === 'SI' ? 'kg' : 'lbs'}</span>
                            </span>
                          </td>
                        );
                      };

                      const ethnicityOptions = [
                        { value: '',                                label: '—' },
                        { value: Ethnicity.WHITE,                   label: Ethnicity.WHITE },
                        { value: Ethnicity.AFRICAN_AMERICAN,        label: Ethnicity.AFRICAN_AMERICAN },
                        { value: Ethnicity.HISPANIC,                label: Ethnicity.HISPANIC },
                        { value: Ethnicity.ASIAN,                   label: Ethnicity.ASIAN },
                        { value: Ethnicity.OTHER,                   label: Ethnicity.OTHER },
                      ];
                      const menopauseOptions = [
                        { value: Menopause.PREMENOPAUSE,  label: 'Premenopause' },
                        { value: Menopause.POSTMENOPAUSE, label: 'Postmenopause' },
                        { value: Menopause.PREGNANT,      label: 'Pregnant' },
                      ];
                      const genderOptions = [
                        { value: Gender.MALE,   label: 'Male' },
                        { value: Gender.FEMALE, label: 'Female' },
                      ];
                      const trimesterOptions = [
                        { value: '',  label: '—' },
                        { value: '1', label: 'First' },
                        { value: '2', label: 'Second' },
                        { value: '3', label: 'Third' },
                      ];

                      return (
                        <>
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', flexWrap: 'wrap' }}>
                          <Table striped bordered hover className="labsmartz-table" style={{ marginBottom: 0, width: 'auto' }}>
                            <thead>
                              <tr>
                                <th>Last Name</th>
                                <th>First Name</th>
                                <th>Sex</th>
                                <th>Collection Date</th>
                                <th>Age at Collection</th>
                                <th>Date of Birth</th>
                                <th>Ethnicity</th>
                                {isFemale && <th>Menopause/Pregnant</th>}
                                {isPregnant && <th>Trimester</th>}
                                <th>Elevation</th>
                                <th>Inhaled Smoke</th>
                                <th>Alcohol Drinker</th>
                                {previousBloodwork && <th>Previous Collection Date</th>}
                                <th>Height</th>
                                <th>Weight</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr className="client-row">
                                {textTd(eff.lastName, (v) => patch((c) => { c.lastName = v; }))}
                                {textTd(eff.firstName, (v) => patch((c) => { c.firstName = v; }))}
                                {selectTd(eff.gender ?? '', genderOptions, eff.displayGender,
                                  (v) => patch((c) => { c.gender = v as Gender; }))}
                                <td title="Collection Date is set when the test is created; edit it from the test entry form."
                                  style={{ verticalAlign: 'middle' }}>
                                  <Moment format="MM/DD/YYYY">{collectionDate}</Moment>
                                </td>
                                <td title="Derived from Date of Birth and Collection Date."
                                  style={{ verticalAlign: 'middle' }}>{eff.ageAsOf(collectionDate)}</td>
                                {dateTd(
                                  eff.dateOfBirth,
                                  <Moment format="MM/DD/YYYY">{eff.dateOfBirth}</Moment>,
                                  (iso) => patch((c) => { c.dateOfBirth = iso; }),
                                )}
                                {selectTd(eff.ethnicity ?? '', ethnicityOptions, eff.displayEthnicity,
                                  (v) => patch((c) => { c.ethnicity = v as Ethnicity; }))}
                                {isFemale && selectTd(
                                  eff.menopause ?? '', menopauseOptions, eff.displayMenopause,
                                  (v) => patch((c) => {
                                    c.menopause = v as Menopause;
                                    if (v !== Menopause.PREGNANT) { c.trimester = undefined; }
                                  }),
                                )}
                                {isPregnant && selectTd(
                                  eff.trimester != null ? String(eff.trimester) : '',
                                  trimesterOptions,
                                  eff.displayTrimester,
                                  (v) => patch((c) => { c.trimester = v ? Number(v) : undefined; }),
                                )}
                                {numberTd(eff.elevation, eff.displayElevation,
                                  (v) => patch((c) => { c.elevation = v; }),
                                  0, 20000, 'feet')}
                                {booleanTd(!!eff.cigaretteSmoker, eff.displayInhaledSmoke,
                                  (v) => patch((c) => { c.cigaretteSmoker = v; }))}
                                {booleanTd(!!eff.alcoholDrinker, eff.displayAlcoholDrinker,
                                  (v) => patch((c) => { c.alcoholDrinker = v; }))}
                                {previousBloodwork && (
                                  <td style={{ verticalAlign: 'middle' }}>
                                    <Moment format="MM/DD/YYYY">{previousBloodwork.collectionDate}</Moment>
                                  </td>
                                )}
                                {heightTd()}
                                {weightTd()}
                              </tr>
                            </tbody>
                          </Table>
                          {this.props.updateClient && (
                            <div style={{ flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 6, alignSelf: 'center' }}>
                              {!editing ? (
                                <button
                                  className="btn btn-sm btn-primary"
                                  onClick={this.startEditClientInfo}
                                >
                                  Edit
                                </button>
                              ) : (
                                <>
                                  <button
                                    className="btn btn-sm btn-primary"
                                    onClick={this.saveClientInfo}
                                    disabled={this.state.isSavingClientInfo}
                                  >
                                    {this.state.isSavingClientInfo ? 'Saving…' : 'Save'}
                                  </button>
                                  <button
                                    className="btn btn-sm btn-secondary"
                                    onClick={this.cancelEditClientInfo}
                                    disabled={this.state.isSavingClientInfo}
                                  >
                                    Cancel
                                  </button>
                                </>
                              )}
                            </div>
                          )}
                        </div>
                        {(() => {
                          const bmiAgeYears = eff.ageAsOf(collectionDate);
                          if (bmiNumber == null || isPregnant) return null;
                          if (bmiAgeYears != null && bmiAgeYears < 2) return null;
                          return (
                            <div style={{ marginTop: 12 }}>
                              <BMIBarChart
                                bmi={bmiNumber}
                                ageYears={bmiAgeYears}
                                sex={bmiSex}
                              />
                            </div>
                          );
                        })()}
                        </>
                      );
                    })()}
                    {this.props.currentUserProfile.preparedBy && (
                      <div style={{ marginTop: 12 }}>
                        <strong>Prepared by:</strong>{' '}
                        {this.props.currentUserProfile.preparedBy}
                      </div>
                    )}
                  </div>
                </Card.Body>
              </Card>
              {this.renderSectionSummary()}

              {(() => {
                // Respect the user's display-mode preference: 'hideSection'
                // removes the panel from the page entirely.
                if (this.props.currentUserProfile?.markersOutsideOptimalDisplay === 'hideSection') {
                  return null;
                }

                const totalOutside =
                  highAlarmGroup.length + lowAlarmGroup.length +
                  aboveLabGroup.length + belowLabGroup.length +
                  aboveOptimalGroup.length + belowOptimalGroup.length;
                if (totalOutside === 0) return null;

                const isPanelExpanded = (key: OutsideOptimalPanelKey) =>
                  !this.state.outsideOptimalChartsCollapsed.includes(key);

                // Zone colors mirror the marker badges so each panel header signals
                // its severity at a glance (red = alarm, orange = lab, yellow = optimal).
                // Pastel panel-header palette — softened tints with dark text
                // (the alarm header moves off white text now that its background
                // is a pale coral). Kept in sync with the PDF zoneColors in
                // bloodwork-analysis-pdf.tsx and the .summary-marker-badge rules
                // in pdf.css.
                const zoneColors = (s: Section): { bg: string; fg: string } => {
                  if (s === Section.NEEDS_ATTENTION)  return { bg: '#F3B7AC', fg: '#7a2e20' };
                  if (s === Section.WORTH_DISCUSSING) return { bg: '#F7D6A9', fg: '#6b4400' };
                  return { bg: '#F1E7A9', fg: '#5a5200' };
                };

                type PanelDef = {
                  key: OutsideOptimalPanelKey;
                  title: string;
                  tooltipId: string;
                  tooltipHeading?: string;
                  tooltip: string;
                  tooltipSortedBy: string;
                  sectionForZone: Section;
                  markers: PanelMarker[];
                  scoreOf: (pm: PanelMarker) => number;
                };

                // Panel order = severity desc, high side first:
                //   High Alarm, Above Lab, Above Optimal, Low Alarm, Below Lab, Below Optimal
                // The first three (highs) stack in the left column and the last three
                // (lows) stack in the right column — see the two-column render below.
                const panelDefs: PanelDef[] = [
                  {
                    key: 'highAlarm',
                    title: 'High Alarm Range',
                    tooltipId: 'panel-tooltip-high-alarm',
                    tooltipHeading: 'High Priority',
                    tooltip: 'Markers whose values have crossed above the high alarm threshold.',
                    tooltipSortedBy: 'Sorted from most to least past the high alarm threshold.',
                    sectionForZone: Section.NEEDS_ATTENTION,
                    markers: highAlarmGroup,
                    scoreOf: pm => zonePenetrationScore(pm, 'alarm'),
                  },
                  {
                    key: 'aboveLab',
                    title: 'Above Lab Range',
                    tooltipId: 'panel-tooltip-above-lab',
                    tooltip: 'Markers above the lab range but not yet in the alarm range.',
                    tooltipSortedBy: 'Sorted from most to least past the upper lab threshold.',
                    sectionForZone: Section.WORTH_DISCUSSING,
                    markers: aboveLabGroup,
                    scoreOf: pm => zonePenetrationScore(pm, 'lab'),
                  },
                  {
                    key: 'aboveOptimal',
                    title: 'Above Optimal Range',
                    tooltipId: 'panel-tooltip-above-optimal',
                    tooltip: 'Markers above the optimal range but still within the lab range, so a standard lab report may not flag them as abnormal.',
                    tooltipSortedBy: 'Sorted from most to least past the upper optimal threshold.',
                    sectionForZone: Section.FINE_TUNE,
                    markers: aboveOptimalGroup,
                    scoreOf: pm => zonePenetrationScore(pm, 'optimal'),
                  },
                  {
                    key: 'lowAlarm',
                    title: 'Low Alarm Range',
                    tooltipId: 'panel-tooltip-low-alarm',
                    tooltipHeading: 'High Priority',
                    tooltip: 'Markers whose values have crossed below the low alarm threshold.',
                    tooltipSortedBy: 'Sorted from most to least past the low alarm threshold.',
                    sectionForZone: Section.NEEDS_ATTENTION,
                    markers: lowAlarmGroup,
                    scoreOf: pm => zonePenetrationScore(pm, 'alarm'),
                  },
                  {
                    key: 'belowLab',
                    title: 'Below Lab Range',
                    tooltipId: 'panel-tooltip-below-lab',
                    tooltip: 'Markers below the lab range but not yet in the alarm range.',
                    tooltipSortedBy: 'Sorted from most to least past the lower lab threshold.',
                    sectionForZone: Section.WORTH_DISCUSSING,
                    markers: belowLabGroup,
                    scoreOf: pm => zonePenetrationScore(pm, 'lab'),
                  },
                  {
                    key: 'belowOptimal',
                    title: 'Below Optimal Range',
                    tooltipId: 'panel-tooltip-below-optimal',
                    tooltip: 'Markers below the optimal range but still within the lab range, so a standard lab report may not flag them as abnormal.',
                    tooltipSortedBy: 'Sorted from most to least past the lower optimal threshold.',
                    sectionForZone: Section.FINE_TUNE,
                    markers: belowOptimalGroup,
                    scoreOf: pm => zonePenetrationScore(pm, 'optimal'),
                  },
                ];

                const renderPanel = (def: PanelDef) => {
                  if (def.markers.length === 0) return null;
                  const expanded = isPanelExpanded(def.key);
                  const colors = zoneColors(def.sectionForZone);
                  return (
                    <Card
                      key={def.key}
                      className="summary-section-card"
                      // Fixed 400px width so every panel is identical width
                      // (axis labels never get clipped). Height is intrinsic so the
                      // panel only grows as tall as its markers need.
                      style={{ width: 400, borderColor: colors.bg }}
                    >
                      <Card.Header
                        onClick={() => this.toggleOutsideOptimalCharts(def.key)}
                        style={{
                          cursor: 'pointer',
                          // .card-header in index.css paints a blue gradient via
                          // background-image, which would mask backgroundColor.
                          backgroundImage: 'none',
                          backgroundColor: colors.bg,
                          color: colors.fg,
                          borderBottomColor: colors.bg,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                        }}
                      >
                        <span>
                          <FontAwesomeIcon icon={expanded ? 'chevron-up' : 'chevron-down'} />
                          &nbsp;&nbsp;{def.title}&nbsp;({def.markers.length})
                        </span>
                        <span onClick={e => e.stopPropagation()}>
                          <OverlayTrigger placement="left" overlay={
                            <Tooltip id={def.tooltipId}>
                              <div style={{ textAlign: 'left' }}>
                                {def.tooltipHeading && (
                                  <div style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>{def.tooltipHeading}</div>
                                )}
                                <div style={{ marginBottom: '0.5rem' }}>{def.tooltip}</div>
                                <div>{def.tooltipSortedBy}</div>
                              </div>
                            </Tooltip>
                          }>
                            <FontAwesomeIcon icon="info-circle" style={{ cursor: 'pointer', color: colors.fg, opacity: colors.fg === '#fff' ? 1 : 0.55 }} />
                          </OverlayTrigger>
                        </span>
                      </Card.Header>
                      <Card.Body>
                        <SummaryDashboardSection
                          title=""
                          section={def.sectionForZone}
                          groups={groupBySystem(def.markers, def.scoreOf)}
                          showCharts={expanded}
                          chartsBelow={true}
                        />
                      </Card.Body>
                    </Card>
                  );
                };

                const wrapperCollapsed = isCollapsed(Section.MARKERS_OUTSIDE_OPTIMAL);

                return (
                  <Card className="mb-3">
                    <Card.Header
                      onClick={() => this.toggleSection(Section.MARKERS_OUTSIDE_OPTIMAL)}
                      style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                    >
                      <span>
                        <FontAwesomeIcon icon={wrapperCollapsed ? 'chevron-down' : 'chevron-up'} />
                        &nbsp;&nbsp;Markers Outside Optimal&nbsp;
                        <span onClick={e => e.stopPropagation()}>
                          <OverlayTrigger placement="right" overlay={
                            <Tooltip id="markers-outside-optimal-tooltip">
                              <div style={{ textAlign: 'left' }}>
                                <div style={{ marginBottom: '0.5rem' }}>All markers that fall outside the optimal range, grouped by severity (alarm / lab / optimal) and direction (high / low).</div>
                                <div style={{ marginBottom: '0.5rem' }}>Panels on the left are higher priority.</div>
                                <div>Click the arrow in any panel header to hide or reveal the reference-range bar charts beneath each marker.</div>
                              </div>
                            </Tooltip>
                          }>
                            <FontAwesomeIcon icon="info-circle" style={{ cursor: 'pointer' }} />
                          </OverlayTrigger>
                        </span>
                      </span>
                      <div style={{ display: 'inline-flex', alignItems: 'center', gap: '16px' }} onClick={e => e.stopPropagation()}>
                        <label
                          htmlFor="show-bar-graphs-MARKERS_OUTSIDE_OPTIMAL"
                          style={{ display: 'inline-flex', alignItems: 'center', margin: 0, fontWeight: 'normal', cursor: 'pointer', color: '#fff' }}
                          title="Show or hide bar graphs in every panel below at once"
                        >
                          <span style={{ marginRight: '6px' }}>Show bar graphs</span>
                          <input
                            id="show-bar-graphs-MARKERS_OUTSIDE_OPTIMAL"
                            type="checkbox"
                            className="lsm-styled-checkbox"
                            checked={this.state.outsideOptimalChartsCollapsed.length === 0}
                            onChange={() => this.toggleAllOutsideOptimalCharts()}
                          />
                        </label>
                        <label
                          htmlFor="include-in-pdf-MARKERS_OUTSIDE_OPTIMAL"
                          style={{ display: 'inline-flex', alignItems: 'center', margin: 0, fontWeight: 'normal', cursor: 'pointer', color: '#fff' }}
                          title="Include this section in the Analysis PDF"
                        >
                          <span style={{ marginRight: '6px' }}>Include in PDF</span>
                          <input
                            id="include-in-pdf-MARKERS_OUTSIDE_OPTIMAL"
                            type="checkbox"
                            className="lsm-styled-checkbox"
                            checked={this.isIncludedInPdf(Section.MARKERS_OUTSIDE_OPTIMAL)}
                            onChange={() => this.toggleIncludeInPdf(Section.MARKERS_OUTSIDE_OPTIMAL)}
                          />
                        </label>
                      </div>
                    </Card.Header>
                    {!wrapperCollapsed && (() => {
                      // Two stacked columns mirroring the PDF: highs on the left
                      // (High Alarm → Above Lab → Above Optimal), lows on the right
                      // (Low Alarm → Below Lab → Below Optimal). Each column is its
                      // own flex stack, so panels pack straight up — a shorter side
                      // never forces a gap, and e.g. Above Optimal sits directly
                      // below Above Lab rather than being pinned to a shared row.
                      // renderPanel() returns null for empty zones, so absent panels
                      // simply collapse out of the stack. On narrow viewports the two
                      // columns wrap to subsequent rows.
                      const columns: { key: string; panels: PanelDef[] }[] = [
                        { key: 'moo-highs', panels: [panelDefs[0], panelDefs[1], panelDefs[2]] },
                        { key: 'moo-lows',  panels: [panelDefs[3], panelDefs[4], panelDefs[5]] },
                      ];
                      return (
                        <Card.Body>
                          <div
                            style={{
                              display: 'flex',
                              flexWrap: 'wrap',
                              gap: '8px',
                              alignItems: 'flex-start',
                            }}
                          >
                            {columns.map(({ key, panels }) => {
                              if (panels.every(p => p.markers.length === 0)) {
                                return null;
                              }
                              return (
                                <div
                                  key={key}
                                  style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: '8px',
                                    flex: '0 0 auto',
                                  }}
                                >
                                  {panels.map(renderPanel)}
                                </div>
                              );
                            })}
                          </div>
                        </Card.Body>
                      );
                    })()}
                  </Card>
                );
              })()}

              <div id={`section-${Section.RBC}`}>
                <BloodworkTestAnalysisSection
                  section={Section.RBC}
                  panelTitle="Blood Oxygen & Red Blood Cell Function"
                  bloodworkAnalysis={bloodworkAnalysis}
                  panelMarkers={withPreviousOnly(bloodworkAnalysis.getRBCPanelMarkers(), a => a.getRBCPanelMarkers())}
                  furtherConsiderations={furtherConsiderations}
                  evalSmall={evalSmall}
                  refSmall={refSmall}
                  evalLarge={evalLarge}
                  refLarge={refLarge}
                  infoLink="https://labsmarts.com/bibliography/#rbc"
                  bloodworkEntryStore={bloodworkEntryStore}
                  enabledSupplementBrands={this.props.currentUserProfile.enabledSupplementBrands}
                  collapsed={isCollapsed(Section.RBC)}
                  onToggle={() => this.toggleSection(Section.RBC)}
                  includeInPdf={this.isIncludedInPdf(Section.RBC)}
                  onIncludeInPdfToggle={() => this.toggleIncludeInPdf(Section.RBC)}
                />
              </div>

              <div id={`section-${Section.IRON}`}>
                <BloodworkTestAnalysisSection
                  section={Section.IRON}
                  panelTitle="Iron Status & Utilization"
                  bloodworkAnalysis={bloodworkAnalysis}
                  panelMarkers={withPreviousOnly(bloodworkAnalysis.getIronPanelMarkers(), a => a.getIronPanelMarkers())}
                  furtherConsiderations={furtherConsiderations}
                  evalSmall={evalSmall}
                  refSmall={refSmall}
                  evalLarge={evalLarge}
                  refLarge={refLarge}
                  infoLink="https://labsmarts.com/bibliography/#iron"
                  bloodworkEntryStore={bloodworkEntryStore}
                  enabledSupplementBrands={this.props.currentUserProfile.enabledSupplementBrands}
                  collapsed={isCollapsed(Section.IRON)}
                  onToggle={() => this.toggleSection(Section.IRON)}
                  includeInPdf={this.isIncludedInPdf(Section.IRON)}
                  onIncludeInPdfToggle={() => this.toggleIncludeInPdf(Section.IRON)}
                />
              </div>

              <div id={`section-${Section.GLUCOSE}`}>
                <BloodworkTestAnalysisSection
                  section={Section.GLUCOSE}
                  panelTitle="Glucose Regulation & Metabolic Function"
                  bloodworkAnalysis={bloodworkAnalysis}
                  panelMarkers={withPreviousOnly(
                    (() => {
                      const all = bloodworkAnalysis.getGlucosePanelMarkers();
                      return isHdlHighSoleOutOfOptimal(all)
                        ? all.filter(pm => pm.attribute !== 'hdl')
                        : all;
                    })(),
                    a => a.getGlucosePanelMarkers(),
                  )}
                  furtherConsiderations={furtherConsiderations}
                  evalSmall={evalSmall}
                  refSmall={refSmall}
                  evalLarge={evalLarge}
                  refLarge={refLarge}
                  infoLink="https://labsmarts.com/bibliography/#glucose"
                  bloodworkEntryStore={bloodworkEntryStore}
                  enabledSupplementBrands={this.props.currentUserProfile.enabledSupplementBrands}
                  collapsed={isCollapsed(Section.GLUCOSE)}
                  onToggle={() => this.toggleSection(Section.GLUCOSE)}
                  includeInPdf={this.isIncludedInPdf(Section.GLUCOSE)}
                  onIncludeInPdfToggle={() => this.toggleIncludeInPdf(Section.GLUCOSE)}
                />
              </div>

              <div id={`section-${Section.LIPIDS}`}>
                <BloodworkTestAnalysisSection
                  section={Section.LIPIDS}
                  panelTitle="Lipid Balance"
                  bloodworkAnalysis={bloodworkAnalysis}
                  panelMarkers={withPreviousOnly(bloodworkAnalysis.getLipidsPanelMarkers(), a => a.getLipidsPanelMarkers())}
                  furtherConsiderations={furtherConsiderations}
                  evalSmall={evalSmall}
                  refSmall={refSmall}
                  evalLarge={evalLarge}
                  refLarge={refLarge}
                  infoLink="https://labsmarts.com/bibliography#lipids"
                  bloodworkEntryStore={bloodworkEntryStore}
                  enabledSupplementBrands={this.props.currentUserProfile.enabledSupplementBrands}
                  collapsed={isCollapsed(Section.LIPIDS)}
                  onToggle={() => this.toggleSection(Section.LIPIDS)}
                  includeInPdf={this.isIncludedInPdf(Section.LIPIDS)}
                  onIncludeInPdfToggle={() => this.toggleIncludeInPdf(Section.LIPIDS)}
                />
              </div>

              <div id={`section-${Section.THYROID}`}>
                <BloodworkTestAnalysisSection
                  section={Section.THYROID}
                  panelTitle="Thyroid Function"
                  bloodworkAnalysis={bloodworkAnalysis}
                  panelMarkers={withPreviousOnly(bloodworkAnalysis.getThyroidPanelMarkers(), a => a.getThyroidPanelMarkers())}
                  furtherConsiderations={furtherConsiderations}
                  evalSmall={evalSmall}
                  refSmall={refSmall}
                  evalLarge={evalLarge}
                  refLarge={refLarge}
                  infoLink="https://labsmarts.com/bibliography#thyroid"
                  bloodworkEntryStore={bloodworkEntryStore}
                  enabledSupplementBrands={this.props.currentUserProfile.enabledSupplementBrands}
                  collapsed={isCollapsed(Section.THYROID)}
                  onToggle={() => this.toggleSection(Section.THYROID)}
                  includeInPdf={this.isIncludedInPdf(Section.THYROID)}
                  onIncludeInPdfToggle={() => this.toggleIncludeInPdf(Section.THYROID)}
                />
              </div>

              <div id={`section-${Section.NUTRIENTS}`}>
                <BloodworkTestAnalysisSection
                  section={Section.NUTRIENTS}
                  panelTitle="Vitamin & Mineral Status"
                  bloodworkAnalysis={bloodworkAnalysis}
                  panelMarkers={withPreviousOnly(bloodworkAnalysis.getMineralPanelMarkers(), a => a.getMineralPanelMarkers())}
                  furtherConsiderations={furtherConsiderations}
                  evalSmall={evalSmall}
                  refSmall={refSmall}
                  evalLarge={evalLarge}
                  refLarge={refLarge}
                  infoLink="https://labsmarts.com/bibliography#nutrients"
                  bloodworkEntryStore={bloodworkEntryStore}
                  enabledSupplementBrands={this.props.currentUserProfile.enabledSupplementBrands}
                  collapsed={isCollapsed(Section.NUTRIENTS)}
                  onToggle={() => this.toggleSection(Section.NUTRIENTS)}
                  includeInPdf={this.isIncludedInPdf(Section.NUTRIENTS)}
                  onIncludeInPdfToggle={() => this.toggleIncludeInPdf(Section.NUTRIENTS)}
                />
              </div>

              <div id={`section-${Section.PROTEINS}`}>
                <BloodworkTestAnalysisSection
                  section={Section.PROTEINS}
                  panelTitle="Protein Status"
                  bloodworkAnalysis={bloodworkAnalysis}
                  panelMarkers={withPreviousOnly(bloodworkAnalysis.getProteinsPanelMarkers(), a => a.getProteinsPanelMarkers())}
                  furtherConsiderations={furtherConsiderations}
                  evalSmall={evalSmall}
                  refSmall={refSmall}
                  evalLarge={evalLarge}
                  refLarge={refLarge}
                  infoLink="https://labsmarts.com/bibliography#proteins"
                  bloodworkEntryStore={bloodworkEntryStore}
                  enabledSupplementBrands={this.props.currentUserProfile.enabledSupplementBrands}
                  collapsed={isCollapsed(Section.PROTEINS)}
                  onToggle={() => this.toggleSection(Section.PROTEINS)}
                  includeInPdf={this.isIncludedInPdf(Section.PROTEINS)}
                  onIncludeInPdfToggle={() => this.toggleIncludeInPdf(Section.PROTEINS)}
                />
              </div>

              {hasWbcValue && (
                <div id={`section-${Section.WBC}`}>
                  <BloodworkTestAnalysisSection
                    section={Section.WBC}
                    panelTitle="Immune Activity"
                    bloodworkAnalysis={bloodworkAnalysis}
                    panelMarkers={withPreviousOnly(
                      (() => {
                        const all = bloodworkAnalysis.getWBCPanelMarkers();
                        const base = this.state.showImmuneAbsolutes ? all : all.filter(pm => !isImmuneAbsolute(pm));
                        return this.state.showOnlyImmuneSectionSignals
                          ? suppressAlpWhenZincSupersedes(base.filter(pm => !isGlobulinOptimal(pm)).filter(keepInImmunePanel), all)
                          : base;
                      })(),
                      a => a.getWBCPanelMarkers(),
                    )}
                    furtherConsiderations={furtherConsiderations}
                    evalSmall={evalSmall}
                    refSmall={refSmall}
                    evalLarge={evalLarge}
                    refLarge={refLarge}
                    infoLink="https://labsmarts.com/bibliography#wbc"
                    bloodworkEntryStore={bloodworkEntryStore}
                    enabledSupplementBrands={this.props.currentUserProfile.enabledSupplementBrands}
                    collapsed={isCollapsed(Section.WBC)}
                    onToggle={() => this.toggleSection(Section.WBC)}
                    includeInPdf={this.isIncludedInPdf(Section.WBC)}
                    onIncludeInPdfToggle={() => this.toggleIncludeInPdf(Section.WBC)}
                    extraToggles={[
                      {
                        id: 'immune-only-section-signals',
                        label: 'Section Signals Only',
                        checked: this.state.showOnlyImmuneSectionSignals,
                        onToggle: this.toggleShowOnlyImmuneSectionSignals,
                        title: 'Only show Immune markers that contribute a signal to this section',
                      },
                      {
                        id: 'immune-show-absolutes',
                        label: 'Show Absolutes',
                        checked: this.state.showImmuneAbsolutes,
                        onToggle: this.toggleShowImmuneAbsolutes,
                        title: 'Show the absolute differential bar graphs (Neutrophils, Lymphocytes, Monocytes, Eosinophils, Basophils)',
                      },
                    ]}
                  />
                </div>
              )}

              <div id={`section-${Section.INFLAMMATION_OXIDATIVE_STRESS}`}>
                <BloodworkTestAnalysisSection
                  section={Section.INFLAMMATION_OXIDATIVE_STRESS}
                  panelTitle="Inflammation & Oxidative Stress"
                  bloodworkAnalysis={bloodworkAnalysis}
                  panelMarkers={withPreviousOnly(
                    (() => {
                      const all = bloodworkAnalysis.getInflammationOxidativeStressPanelMarkers();
                      return this.props.currentUserProfile.showOnlySectionSignals
                        ? all.filter(keepInInflammationPanel)
                        : all;
                    })(),
                    a => a.getInflammationOxidativeStressPanelMarkers(),
                  )}
                  furtherConsiderations={furtherConsiderations}
                  evalSmall={evalSmall}
                  refSmall={refSmall}
                  evalLarge={evalLarge}
                  refLarge={refLarge}
                  infoLink="https://labsmarts.com/bibliography#inflammation"
                  bloodworkEntryStore={bloodworkEntryStore}
                  enabledSupplementBrands={this.props.currentUserProfile.enabledSupplementBrands}
                  collapsed={isCollapsed(Section.INFLAMMATION_OXIDATIVE_STRESS)}
                  onToggle={() => this.toggleSection(Section.INFLAMMATION_OXIDATIVE_STRESS)}
                  includeInPdf={this.isIncludedInPdf(Section.INFLAMMATION_OXIDATIVE_STRESS)}
                  onIncludeInPdfToggle={() => this.toggleIncludeInPdf(Section.INFLAMMATION_OXIDATIVE_STRESS)}
                />
              </div>

              <div id={`section-${Section.PLATELETS}`}>
                <BloodworkTestAnalysisSection
                  section={Section.PLATELETS}
                  panelTitle="Blood Clotting Function"
                  bloodworkAnalysis={bloodworkAnalysis}
                  panelMarkers={withPreviousOnly(bloodworkAnalysis.getPlateletPanelMarkers(), a => a.getPlateletPanelMarkers())}
                  furtherConsiderations={furtherConsiderations}
                  evalSmall={evalSmall}
                  refSmall={refSmall}
                  evalLarge={evalLarge}
                  refLarge={refLarge}
                  infoLink="https://labsmarts.com/bibliography#platelets"
                  bloodworkEntryStore={bloodworkEntryStore}
                  enabledSupplementBrands={this.props.currentUserProfile.enabledSupplementBrands}
                  collapsed={isCollapsed(Section.PLATELETS)}
                  onToggle={() => this.toggleSection(Section.PLATELETS)}
                  includeInPdf={this.isIncludedInPdf(Section.PLATELETS)}
                  onIncludeInPdfToggle={() => this.toggleIncludeInPdf(Section.PLATELETS)}
                />
              </div>

              <div id={`section-${Section.LIVER_BILIARY}`}>
                <BloodworkTestAnalysisSection
                  section={Section.LIVER_BILIARY}
                  panelTitle="Liver Function"
                  bloodworkAnalysis={bloodworkAnalysis}
                  panelMarkers={withPreviousOnly(bloodworkAnalysis.getLiverPanelMarkers(), a => a.getLiverPanelMarkers())}
                  furtherConsiderations={furtherConsiderations}
                  evalSmall={evalSmall}
                  refSmall={refSmall}
                  evalLarge={evalLarge}
                  refLarge={refLarge}
                  infoLink="https://labsmarts.com/bibliography#liver"
                  bloodworkEntryStore={bloodworkEntryStore}
                  enabledSupplementBrands={this.props.currentUserProfile.enabledSupplementBrands}
                  collapsed={isCollapsed(Section.LIVER_BILIARY)}
                  onToggle={() => this.toggleSection(Section.LIVER_BILIARY)}
                  includeInPdf={this.isIncludedInPdf(Section.LIVER_BILIARY)}
                  onIncludeInPdfToggle={() => this.toggleIncludeInPdf(Section.LIVER_BILIARY)}
                />
              </div>

              <div id={`section-${Section.KIDNEY}`}>
                <BloodworkTestAnalysisSection
                  section={Section.KIDNEY}
                  panelTitle="Kidney Function"
                  bloodworkAnalysis={bloodworkAnalysis}
                  panelMarkers={withPreviousOnly(bloodworkAnalysis.getKidneyPanelMarkers(), a => a.getKidneyPanelMarkers())}
                  furtherConsiderations={furtherConsiderations}
                  evalSmall={evalSmall}
                  refSmall={refSmall}
                  evalLarge={evalLarge}
                  refLarge={refLarge}
                  infoLink="https://labsmarts.com/bibliography#kidney"
                  bloodworkEntryStore={bloodworkEntryStore}
                  enabledSupplementBrands={this.props.currentUserProfile.enabledSupplementBrands}
                  collapsed={isCollapsed(Section.KIDNEY)}
                  onToggle={() => this.toggleSection(Section.KIDNEY)}
                  includeInPdf={this.isIncludedInPdf(Section.KIDNEY)}
                  onIncludeInPdfToggle={() => this.toggleIncludeInPdf(Section.KIDNEY)}
                />
              </div>

              <div id={`section-${Section.ELECTROLYTES}`}>
                <BloodworkTestAnalysisSection
                  section={Section.ELECTROLYTES}
                  panelTitle="Electrolyte Balance"
                  bloodworkAnalysis={bloodworkAnalysis}
                  panelMarkers={withPreviousOnly(bloodworkAnalysis.getElectrolytePanelMarkers(), a => a.getElectrolytePanelMarkers())}
                  furtherConsiderations={furtherConsiderations}
                  evalSmall={evalSmall}
                  refSmall={refSmall}
                  evalLarge={evalLarge}
                  refLarge={refLarge}
                  infoLink="https://labsmarts.com/bibliography#electrolytes"
                  bloodworkEntryStore={bloodworkEntryStore}
                  enabledSupplementBrands={this.props.currentUserProfile.enabledSupplementBrands}
                  collapsed={isCollapsed(Section.ELECTROLYTES)}
                  onToggle={() => this.toggleSection(Section.ELECTROLYTES)}
                  includeInPdf={this.isIncludedInPdf(Section.ELECTROLYTES)}
                  onIncludeInPdfToggle={() => this.toggleIncludeInPdf(Section.ELECTROLYTES)}
                />
              </div>

              <div id={`section-${Section.STRESS_ADRENAL}`}>
                <BloodworkTestAnalysisSection
                  section={Section.STRESS_ADRENAL}
                  panelTitle="Stress & Adrenal Balance"
                  bloodworkAnalysis={bloodworkAnalysis}
                  panelMarkers={withPreviousOnly(bloodworkAnalysis.getStressAdrenalPanelMarkers(), a => a.getStressAdrenalPanelMarkers())}
                  furtherConsiderations={furtherConsiderations}
                  evalSmall={evalSmall}
                  refSmall={refSmall}
                  evalLarge={evalLarge}
                  refLarge={refLarge}
                  infoLink="https://labsmarts.com/bibliography#stress-adrenal"
                  bloodworkEntryStore={bloodworkEntryStore}
                  enabledSupplementBrands={this.props.currentUserProfile.enabledSupplementBrands}
                  collapsed={isCollapsed(Section.STRESS_ADRENAL)}
                  onToggle={() => this.toggleSection(Section.STRESS_ADRENAL)}
                  includeInPdf={this.isIncludedInPdf(Section.STRESS_ADRENAL)}
                  onIncludeInPdfToggle={() => this.toggleIncludeInPdf(Section.STRESS_ADRENAL)}
                />
              </div>

              <div id={`section-${Section.SEX_HORMONE}`}>
                <BloodworkTestAnalysisSection
                  section={Section.SEX_HORMONE}
                  panelTitle="Hormone Balance"
                  bloodworkAnalysis={bloodworkAnalysis}
                  panelMarkers={withPreviousOnly(bloodworkAnalysis.getSexHormonePanelMarkers(), a => a.getSexHormonePanelMarkers())}
                  furtherConsiderations={furtherConsiderations}
                  evalSmall={evalSmall}
                  refSmall={refSmall}
                  evalLarge={evalLarge}
                  refLarge={refLarge}
                  infoLink="https://labsmarts.com/bibliography#hormones"
                  bloodworkEntryStore={bloodworkEntryStore}
                  enabledSupplementBrands={this.props.currentUserProfile.enabledSupplementBrands}
                  collapsed={isCollapsed(Section.SEX_HORMONE)}
                  onToggle={() => this.toggleSection(Section.SEX_HORMONE)}
                  includeInPdf={this.isIncludedInPdf(Section.SEX_HORMONE)}
                  onIncludeInPdfToggle={() => this.toggleIncludeInPdf(Section.SEX_HORMONE)}
                />
              </div>

              <div id={`section-${Section.PROSTATE}`}>
                <BloodworkTestAnalysisSection
                  section={Section.PROSTATE}
                  panelTitle="Prostate Function"
                  bloodworkAnalysis={bloodworkAnalysis}
                  panelMarkers={withPreviousOnly(bloodworkAnalysis.getProstatePanelMarkers(), a => a.getProstatePanelMarkers())}
                  furtherConsiderations={furtherConsiderations}
                  evalSmall={evalSmall}
                  refSmall={refSmall}
                  evalLarge={evalLarge}
                  refLarge={refLarge}
                  infoLink="https://labsmarts.com/bibliography#prostate"
                  bloodworkEntryStore={bloodworkEntryStore}
                  enabledSupplementBrands={this.props.currentUserProfile.enabledSupplementBrands}
                  collapsed={isCollapsed(Section.PROSTATE)}
                  onToggle={() => this.toggleSection(Section.PROSTATE)}
                  includeInPdf={this.isIncludedInPdf(Section.PROSTATE)}
                  onIncludeInPdfToggle={() => this.toggleIncludeInPdf(Section.PROSTATE)}
                />
              </div>

              {/* Immune & Inflammatory Burden Index (I³) — questionnaire-driven.
                  One row: the Weighted Score + Root Cause Framework panels are
                  FIXED widths; the Assessment/Testing/Intervention tabs flex to
                  fill the remaining width. Env-gated to local dev only (hidden in
                  slava + prod). */}
              {process.env.REACT_APP_ENV !== 'prod' &&
                process.env.REACT_APP_ENV !== 'slava' &&
                ImmuneInflammatoryBurdenPanel.shouldRender(bloodworkAnalysis) && (
                  <div id={`section-${Section.IMMUNE_INFLAMMATORY}`}>
                    <Card>
                      <Card.Header
                        onClick={() => this.toggleSection(Section.IMMUNE_INFLAMMATORY)}
                        style={{ cursor: 'pointer' }}
                      >
                        <FontAwesomeIcon icon={isCollapsed(Section.IMMUNE_INFLAMMATORY) ? 'chevron-down' : 'chevron-up'} />
                        &nbsp;&nbsp;Immune &amp; Inflammatory Burden Index (I³)
                        <span style={{ fontWeight: 400, fontSize: '13px', opacity: 0.85 }}>
                          {' '}— Created by Robert Rountree, MD · Jocelyn Strand, ND · BWH Labs
                        </span>
                      </Card.Header>
                      {!isCollapsed(Section.IMMUNE_INFLAMMATORY) && (
                        <Card.Body>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', alignItems: 'flex-start' }}>
                            <div style={{ flex: '0 0 auto', width: '440px', maxWidth: '100%' }}>
                              <ImmuneInflammatoryBurdenPanel bloodworkAnalysis={bloodworkAnalysis} />
                            </div>
                            <div style={{ flex: '0 0 auto', width: '340px', maxWidth: '100%' }}>
                              <RootCauseFrameworkPanel bloodworkAnalysis={bloodworkAnalysis} />
                            </div>
                            <div style={{ flex: '1 1 360px', minWidth: '320px' }}>
                              <RootCauseGuidanceTabs bloodworkAnalysis={bloodworkAnalysis} />
                            </div>
                          </div>
                        </Card.Body>
                      )}
                    </Card>
                  </div>
                )}

              {/* Metabolic Vulnerability (MVX Plus). Reads the nine MVX values off
                  the current bloodwork (entered via the MVX Plus card in Add Test
                  Results); renders only when at least one is entered. Env-gated to
                  local dev only (hidden in slava + prod). */}
              {process.env.REACT_APP_ENV !== 'prod' &&
                process.env.REACT_APP_ENV !== 'slava' &&
                MetabolicVulnerabilityInsights.shouldRender(bloodworkAnalysis) && (
                  <Card id="section-mvx">
                    <Card.Header
                      onClick={() => this.toggleSection(MVX_SECTION)}
                      style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                    >
                      <span>
                        <FontAwesomeIcon icon={isCollapsed(MVX_SECTION) ? 'chevron-down' : 'chevron-up'} />
                        &nbsp;&nbsp;Metabolic Vulnerability Index (MVX) Plus&nbsp;&nbsp;
                        <span onClick={e => e.stopPropagation()} style={{ display: 'inline-block' }}>
                          <OverlayTrigger
                            placement="right"
                            trigger="click"
                            rootClose
                            overlay={
                              <Popover id="mvx-section-tooltip" style={{ maxWidth: 306 }}>
                                <Popover.Content>
                                  <div style={{ textAlign: 'left' }}>
                                    The <strong>Metabolic Vulnerability Index (MVX) Plus</strong> is a Labcorp NMR
                                    blood test (#123601) that scores near-term metabolic resilience from six markers,
                                    combined into two sub-scores — inflammation (IVX) and metabolic strain (MMX).
                                    <div style={{ marginTop: 6 }}>
                                      <a href={LABCORP_MVX_PLUS_URL} target="_blank" rel="noopener noreferrer">
                                        Learn more
                                      </a>
                                    </div>
                                  </div>
                                </Popover.Content>
                              </Popover>
                            }
                          >
                            <FontAwesomeIcon icon="info-circle" style={{ color: '#fff', cursor: 'pointer' }} />
                          </OverlayTrigger>
                        </span>
                      </span>
                      <label
                        htmlFor="include-in-pdf-mvx"
                        onClick={e => e.stopPropagation()}
                        style={{ display: 'inline-flex', alignItems: 'center', margin: 0, fontWeight: 'normal', cursor: 'pointer', color: '#fff' }}
                        title="Include this section in the Analysis PDF"
                      >
                        <span style={{ marginRight: '6px' }}>Include in PDF</span>
                        <input
                          id="include-in-pdf-mvx"
                          type="checkbox"
                          className="lsm-styled-checkbox"
                          checked={this.isIncludedInPdf(MVX_SECTION)}
                          onChange={() => this.toggleIncludeInPdf(MVX_SECTION)}
                        />
                      </label>
                    </Card.Header>
                    {!isCollapsed(MVX_SECTION) && (
                      <>
                        <Card.Body>
                          <Row>
                            <Col xs={12} lg={3}>
                              <MetabolicVulnerabilityInsights bloodworkAnalysis={bloodworkAnalysis} />
                            </Col>
                            <Col xs={12} lg={9}>
                              <Tabs defaultActiveKey="mvx-reference" id="mvx-section-tabs" mountOnEnter>
                                <Tab eventKey="mvx-reference" title="Reference Range">
                                  <div style={{ paddingTop: 12 }}>
                                    <MvxResultsPanel bloodworkAnalysis={bloodworkAnalysis} />
                                  </div>
                                </Tab>
                                <Tab eventKey="mvx-references" title="References">
                                  <div style={{ paddingTop: 12 }}>
                                    <MvxReferencesSection />
                                  </div>
                                </Tab>
                                <Tab eventKey="mvx-about" title="About">
                                  <div style={{ paddingTop: 12 }}>
                                    <MvxAboutSection bloodworkAnalysis={bloodworkAnalysis} />
                                  </div>
                                </Tab>
                              </Tabs>
                            </Col>
                          </Row>
                          <MvxBlueprintPanel bloodworkAnalysis={bloodworkAnalysis} furtherConsiderations={furtherConsiderations} />
                        </Card.Body>
                        <Card.Footer>
                          <Footer />
                        </Card.Footer>
                      </>
                    )}
                  </Card>
                )}

              <SectionNotesSummaryPanel
                bloodworkAnalysis={bloodworkAnalysis}
                includeInPdf={this.isIncludedInPdf(Section.SECTION_NOTES)}
                onIncludeInPdfToggle={() =>
                  this.toggleIncludeInPdf(Section.SECTION_NOTES)
                }
                bloodworkEntryStore={bloodworkEntryStore}
              />

              <Card id="insights">
                <Card.Header
                  onClick={() => this.toggleSection(Section.NOTES)}
                  style={{ cursor: 'pointer' }}
                >
                  <FontAwesomeIcon icon={isCollapsed(Section.NOTES) ? 'chevron-down' : 'chevron-up'} />
                  &nbsp;&nbsp;Insights
                </Card.Header>
                {!isCollapsed(Section.NOTES) && (
                  <Card.Body>
                    <BloodworkNotes
                      section={Section.NOTES}
                      title="Add your own notes here. Click Save when finished."
                      bloodwork={bloodworkAnalysis.bloodwork}
                      bloodworkEntryStore={bloodworkEntryStore}
                    />
                  </Card.Body>
                )}
              </Card>

              {supplementsVisible() &&
                hasSupplementProtocolsContent(
                  bloodworkAnalysis,
                  furtherConsiderations,
                  this.props.currentUserProfile.enabledSupplementBrands,
                ) && (
                  <Card className="mb-3" id="supplement-protocols">
                    <Card.Header>
                      <span>Holobiome Roadmap — Systemic Formulas (Dr. Shane Morris)&nbsp;&nbsp;</span>
                      <OverlayTrigger
                        placement="right"
                        trigger="click"
                        rootClose
                        overlay={
                          <Popover id="supplement-protocols-tooltip">
                            <Popover.Content>
                              <div style={{ textAlign: 'left' }}>
                                The five Systemic Formulas Holobiome kits, in
                                roadmap order. Each kit is bought whole, so every
                                product it contains is listed.{' '}
                                <strong>Highlighted</strong> products are the
                                ones a pattern identified in this test points to
                                — the rest still come in the kit.
                                <div style={{ marginTop: 6 }}>
                                  <strong>Add-On Products</strong> lists the
                                  Systemic Formulas recommendations that aren't
                                  part of any kit.
                                </div>
                                <div style={{ marginTop: 6 }}>
                                  <a
                                    href="https://systemicformulas.com/holobiome-roadmap"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  >
                                    Learn more
                                  </a>
                                </div>
                              </div>
                            </Popover.Content>
                          </Popover>
                        }
                      >
                        <FontAwesomeIcon
                          icon="info-circle"
                          style={{ color: '#fff', cursor: 'pointer' }}
                        />
                      </OverlayTrigger>
                    </Card.Header>
                    <Card.Body>
                      <SupplementProtocolsPanel
                        bloodworkAnalysis={bloodworkAnalysis}
                        furtherConsiderations={furtherConsiderations}
                        enabledSupplementBrands={
                          this.props.currentUserProfile.enabledSupplementBrands
                        }
                        onSectionClick={this.jumpToSection}
                      />
                    </Card.Body>
                  </Card>
                )}

              {supplementsVisible() && (
                <SupplementsSummaryPanel
                  bloodworkAnalysis={bloodworkAnalysis}
                  furtherConsiderations={furtherConsiderations}
                  enabledSupplementBrands={
                    this.props.currentUserProfile.enabledSupplementBrands
                  }
                  includeInPdf={this.isIncludedInPdf(Section.SUPPLEMENTS)}
                  onIncludeInPdfToggle={() =>
                    this.toggleIncludeInPdf(Section.SUPPLEMENTS)
                  }
                  bloodworkEntryStore={bloodworkEntryStore}
                />
              )}

              <DisclaimerSection furtherConsiderations={furtherConsiderations} />
            </Card.Body>
          )}
        </Card>

        <GenericModal
          show={showUpgradeModal}
          title="Export to PDF Feature Not Available"
          text="Export to PDF feature is not available with a free account."
          link="https://labsmarts.com/pricing"
          linkText="Click here to upgrade."
          onHide={() => {
            this.setState({ showUpgradeModal: false });
          }}
        />


        <OverlayTrigger
          placement="left"
          overlay={
            <Tooltip id="return-to-results-overview-tooltip">
              Return to Results Overview
            </Tooltip>
          }
        >
          <button
            onClick={() => {
              const element = document.getElementById('results-overview');
              if (!element) return;
              const sticky = document.querySelector('.App-sticky-top') as HTMLElement | null;
              const stickyHeight = sticky ? sticky.getBoundingClientRect().height : 0;
              const top = element.getBoundingClientRect().top + window.scrollY - stickyHeight;
              window.scrollTo({ top, behavior: 'smooth' });
            }}
            style={{
              // Stacked above the "Jump down to Section Notes" button (44px
              // circle + 8px gap = 52px), keeping the pair anchored bottom-right.
              position: 'fixed',
              bottom: '84px',
              right: '32px',
              width: '44px',
              height: '44px',
              borderRadius: '50%',
              backgroundColor: '#ff9a9a',
              //766d66 - old taupe circle color; 337ab7 - old blue color
              border: 'none',
              padding: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
              zIndex: 1050,
            }}
          >
            <FontAwesomeIcon
              icon={faArrowUp}
              style={{
                fontSize: '18px',
                color: '#fff',
              }}
            />
          </button>
        </OverlayTrigger>

        <OverlayTrigger
          placement="left"
          overlay={
            <Tooltip id="jump-to-section-notes-tooltip">
              Jump to Section Notes / Insights
            </Tooltip>
          }
        >
          <button
            onClick={() => {
              // Section Notes renders nothing when empty; fall back to the
              // Insights section so the button always lands somewhere useful.
              const element =
                document.getElementById('section-notes') ||
                document.getElementById('insights');
              if (!element) return;
              const sticky = document.querySelector('.App-sticky-top') as HTMLElement | null;
              const stickyHeight = sticky ? sticky.getBoundingClientRect().height : 0;
              const top = element.getBoundingClientRect().top + window.scrollY - stickyHeight;
              window.scrollTo({ top, behavior: 'smooth' });
            }}
            style={{
              position: 'fixed',
              bottom: '32px',
              right: '32px',
              width: '44px',
              height: '44px',
              borderRadius: '50%',
              backgroundColor: '#ff9a9a',
              border: 'none',
              padding: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
              zIndex: 1050,
            }}
          >
            <FontAwesomeIcon
              icon={faArrowDown}
              style={{
                fontSize: '18px',
                color: '#fff',
              }}
            />
          </button>
        </OverlayTrigger>
      </>
    );
  }
}
